import { shouldUseSharedView } from '../../lib/projectAccess'
import { TAB_LABELS, reviewTabIsPrimary } from './projectTabs'
import { useState, useEffect, useCallback, useMemo, useRef, type DragEvent, type KeyboardEvent as ReactKeyboardEvent, type ReactNode } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Upload, Home, Layers, FolderPlus, Users, UserPlus, FileUp, CheckCircle, AlertCircle, AlertTriangle, X, Crown, FileX, LayoutGrid, LayoutDashboard, List, Sparkles, FileText, Search, Shuffle, Network, Zap, GitBranch, StickyNote, Tag, Puzzle, Clock, Users2, Share2, MoreHorizontal, ClipboardCheck, Compass, BarChart3, type LucideIcon } from 'lucide-react'
import { getProjects, getProject, getDocuments, getFolders, createFolder, updateFolder, deleteFolder, getPermissions, revokePermission, getUploadUrl, confirmUpload, checkManagerAccess, getProjectOwners, addProjectOwner, removeProjectOwner, reprocessProject, getProjectAnalysis, updateProject, getProjectPriorArt, getAssessment, getMatchSummary, getDisclosureAgreements, approveProjectReview, getFolderListItems, getFolderNotes, getCachedPortfolioOverlap, getProjectClaims, downloadDocument } from '../../services/api'
import axios from 'axios'
import { useAuth } from '../../contexts/AuthContext'
import { useAggregator } from '../../contexts/AggregatorContext'
import type { Folder, MatchSummary, DisclosureAgreement, Project, Document as InventionDocument } from '../../types'
import type { FolderAction } from '../../components/documents/FolderTree'
import FolderRow from '../../components/documents/FolderRow'
import DocumentCard from '../../components/documents/DocumentCard'
import DocumentGrid from '../../components/documents/DocumentGrid'
import ShareModal from '../../components/documents/ShareModal'
import CollaboratorPermissionsModal from '../../components/documents/CollaboratorPermissionsModal'
import InlineConnectionsPanel from '../../components/graph/InlineConnectionsPanel'
import CollaborationPanel from '../../components/collaboration/CollaborationPanel'
import PageHeader from '../../components/ui/PageHeader'
import HelpPopover from '../../components/help/HelpPopover'
import { useSetSpine, NextStepBanner } from '../../components/spine'
import LensSwitch from '../../components/lens/LensSwitch'
import ClaimsLens from '../../components/lens/ClaimsLens'
import { resolveFocusIndex } from '../../components/lens/claimFocus'
import PipelineStepper from '../../components/nextstep/PipelineStepper'
import RelatedInYourSet from '../../components/nextstep/RelatedInYourSet'
import OrchestratorTab from '../../components/orchestrator/OrchestratorTab'
import { type WorkflowStep } from '../../components/ui/WorkflowStepper'
import { deriveAssessmentState, INSUFFICIENT_BASIS_REASON } from '../../components/orchestrator/assessmentScores'
import { computeWorkflowStages, hasReachedStage } from '../../components/orchestrator/workflowStages'
import ProjectWhatNextPanel from '../../components/orchestrator/ProjectWhatNextPanel'
import type { ProjectTab } from '../../components/documents/ProjectListTabs'
import ProjectReview from '../../components/reviews/ProjectReview'
import FolderNotesList from '../../components/documents/FolderNotesList'
import RelatedJournalsPanel from '../../components/documents/RelatedJournalsPanel'
import ProjectActivityTimeline from '../../components/documents/ProjectActivityTimeline'
import ProjectAIAnalysisPanel from '../../components/documents/ProjectAIAnalysisPanel'
import ProjectPriorArtPanel from '../../components/documents/ProjectPriorArtPanel'
import ReferenceComponentsList from '../../components/documents/ReferenceComponentsList'
import { aggregateProjectTopics } from '../../utils/topics'
import { granteeLabel, granteeInitial } from '../../utils/collaborators'
import { looksLikeCiphertext, fieldDecryptFailed } from '../../utils/encryption'
import { uploadLinkFor } from '../../utils/uploadLink'
import { countOrNone, noRetryOn404 } from '../../utils/countOrNone'
import LoadingSkeleton from '../../components/shared/LoadingSkeleton'
import EmptyState from '../../components/shared/EmptyState'
import ConfirmDialog from '../../components/shared/ConfirmDialog'
import AggregatorSharingPanel from '../../components/projects/AggregatorSharingPanel'
import MatchesTab from '../../components/projects/MatchesTab'
import DisclosureLadder from '../../components/project/DisclosureLadder'
import ProjectContextRail from '../../components/project/ProjectContextRail'
import InventionIntakeCard from '../../components/project/InventionIntakeCard'
import AssessmentPanel, { type AssessmentAxis } from '../../components/project/AssessmentPanel'
import WhatNextSuggestions from '../../components/project/WhatNextSuggestions'
import HeaderActionMenu from '../../components/project/HeaderActionMenu'
import WhiteSpaceTab from '../../components/whitespace/WhiteSpaceTab'
import MarketTab from '../../components/market/MarketTab'

// A single, reusable progressive-disclosure tab bar (Spec P2). A small set of
// PRIMARY tabs stay visible; everything else lives behind a "More" overflow menu
// so no tab is ever removed — the flat 13-tab wall is just re-presented. The
// active tab is always surfaced in the bar even when it lives in the overflow,
// so the user never loses their place. Generic over the tab-key union so it can
// drive both the project and document workspaces.
export interface TabItem<T extends string> {
  key: T
  label: string
  icon: LucideIcon
  /** optional count badge (0 / undefined renders nothing) */
  count?: number
  /** conditionally hide a tab without deleting it from the list */
  hidden?: boolean
  /** UX-020: this tab holds work waiting on the user; when it sits behind More, More says so. */
  pending?: boolean
}



/**
 * Who may see the "Approve & unlock matching" control. The backend route is
 * owner-only, so the control is owner-only too: a collaborator or a read-only
 * aggregator viewer gets the sentence, never a button that 403s on click
 * (White-Space UX review, 6 Sep 2026).
 */
export function canApproveReview(isOwner: boolean, hasAssessmentScores: boolean, reviewStatus: string | undefined): boolean {
  return isOwner && hasAssessmentScores && reviewStatus !== 'approved'
}

export function ProgressiveTabBar<T extends string>({
  tabs,
  primaryKeys,
  activeTab,
  onTabChange,
  moreLabel = 'More',
}: {
  tabs: TabItem<T>[]
  primaryKeys: T[]
  activeTab: T
  onTabChange: (tab: T) => void
  moreLabel?: string
}) {
  const [moreOpen, setMoreOpen] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const moreBtnRef = useRef<HTMLButtonElement>(null)
  const itemsRef = useRef<HTMLDivElement>(null)

  // UX-020: opening More moves focus into the menu (the active item if it is
  // there, else the first), so a keyboard user is not left on a button whose
  // menu they cannot reach.
  useEffect(() => {
    if (!moreOpen) return
    const items = itemsRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]')
    if (!items || items.length === 0) return
    const current = Array.from(items).find((el) => el.getAttribute('aria-current') === 'page')
    ;(current ?? items[0]).focus()
  }, [moreOpen])

  function onMenuKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    const items = Array.from(itemsRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [])
    if (items.length === 0) return
    const at = items.indexOf(document.activeElement as HTMLElement)
    let next = -1
    if (e.key === 'ArrowDown') next = (at + 1) % items.length
    else if (e.key === 'ArrowUp') next = (at - 1 + items.length) % items.length
    else if (e.key === 'Home') next = 0
    else if (e.key === 'End') next = items.length - 1
    if (next >= 0) {
      e.preventDefault()
      items[next].focus()
    }
  }

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMoreOpen(false)
    }
    function onEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setMoreOpen(false)
        moreBtnRef.current?.focus()
      }
    }
    if (moreOpen) {
      document.addEventListener('mousedown', onDoc)
      document.addEventListener('keydown', onEsc)
    }
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onEsc)
    }
  }, [moreOpen])

  const visible = tabs.filter((t) => !t.hidden)
  const primary = visible.filter((t) => primaryKeys.includes(t.key))
  const overflow = visible.filter((t) => !primaryKeys.includes(t.key))
  const activeInOverflow = overflow.find((t) => t.key === activeTab)
  const pendingBehindMore = overflow.filter((t) => t.pending).length

  const renderTab = (t: TabItem<T>, inMenu: boolean) => {
    const isActive = t.key === activeTab
    const Icon = t.icon
    return (
      <button
        key={t.key}
        type="button"
        role={inMenu ? 'menuitem' : undefined}
        aria-current={!inMenu && isActive ? 'page' : undefined}
        onClick={() => { onTabChange(t.key); setMoreOpen(false) }}
        className={
          inMenu
            ? `flex w-full items-center gap-2 px-3 py-2 text-sm text-left ${isActive ? 'bg-iflow-50 text-iflow-700 font-medium' : 'text-gray-600 hover:bg-sky-50'}`
            : `flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${isActive ? 'border-iflow-500 text-iflow-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`
        }
      >
        {/* The figures draw the strip as words only; the icon stays in the
            overflow menu, where a row is a list item rather than a tab. */}
        {inMenu && <Icon className="w-4 h-4" />}
        {t.label}
        {t.count != null && t.count > 0 && (
          <span className={`ml-1 px-1.5 py-0.5 text-xs rounded-full ${isActive ? 'bg-iflow-100 text-iflow-700' : 'bg-sky-500/10 text-gray-600'}`}>
            {t.count}
          </span>
        )}
      </button>
    )
  }

  return (
    <div className="relative flex flex-wrap items-center border-b border-sky-200/50" data-testid="tab-bar">
      {primary.map((t) => renderTab(t, false))}
      {/* Surface the active overflow tab so the user's current location stays visible */}
      {activeInOverflow && renderTab(activeInOverflow, false)}
      {overflow.length > 0 && (
        <div className="relative ml-auto" ref={menuRef}>
          <button
            type="button"
            ref={moreBtnRef}
            aria-haspopup="menu"
            aria-expanded={moreOpen}
            onClick={() => setMoreOpen((o) => !o)}
            className={`flex items-center gap-1.5 px-3 py-2.5 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
              activeInOverflow ? 'border-iflow-500 text-iflow-600' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <MoreHorizontal className="w-4 h-4" />
            {moreLabel}
            {pendingBehindMore > 0 && (
              <span
                data-testid="more-pending-badge"
                aria-label={`${pendingBehindMore} waiting`}
                className="ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-800"
              >
                {pendingBehindMore}
              </span>
            )}
          </button>
          {/* Story #6766: More sits at the bar's right end (ml-auto), so the menu
              hangs from its RIGHT edge. Hung from the left it ran off the page. */}
          {moreOpen && (
            <div role="menu" ref={itemsRef} onKeyDown={onMenuKeyDown} className="absolute right-0 top-full z-20 mt-1 min-w-[13rem] max-w-[calc(100vw-2rem)] rounded-lg border border-sky-200 bg-white py-1 shadow-xl">
              {overflow.map((t) => renderTab(t, true))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

/** The Claims lens's tab keys: its own Claims tab plus the invention's. */
type LensTab = ProjectTab | 'claims'
const LENS_PRIMARY: LensTab[] = ['claims', 'research', 'ai-analysis', 'next-steps']

/**
 * The figure's own pill, read out of kit.css rather than eyeballed (round 9).
 *
 *   .pill{display:inline-flex;align-items:center;gap:5px;font-size:11px;
 *         font-weight:600;border-radius:999px;padding:3px 10px}
 *   .pill-neutral{background:#EEF2F6;color:var(--muted)}   --muted:#64748B
 *   .pill-warn   {background:#FEF3C7;color:#B45309}
 *   .pill-danger {background:#FEE2E2;color:#DC2626}
 *
 * There is NO border on any of them. Live drew every branch with a 1px border
 * and a lighter #F9FAFB fill, which is the difference the round-8 walk
 * measured on S3's "Matching pending".
 *
 * #64748B is exactly Tailwind's slate-500, so that one is named; the fills have
 * no Tailwind equivalent and are written as the hexes the figure uses.
 */
const PILL = 'inline-flex items-center gap-[5px] rounded-full px-2.5 py-[3px] text-[11px] font-semibold'
const PILL_NEUTRAL = `${PILL} bg-[#EEF2F6] text-slate-500`
const PILL_WARN = `${PILL} bg-[#FEF3C7] text-[#B45309]`
const PILL_DANGER = `${PILL} bg-[#FEE2E2] text-[#DC2626]`

/**
 * The 🔗 the figure puts in front of the count.
 *
 * It rides the three NEUTRAL states — the count, "Matching pending" and "No
 * overlaps yet" — because they are one control in one slot saying what the
 * matching slot currently knows, and the figure draws that slot with a glyph.
 * It is deliberately absent from "Computing matches…" and "Match error", which
 * are transient conditions rather than the matching statement, and where a link
 * glyph in front of the word "error" would read as an error about a link.
 *
 * aria-hidden so the accessible name stays "3 potential matches" and a screen
 * reader is never made to announce "link" for a decorative character.
 */
function PillLink() {
  return <span aria-hidden>🔗</span>
}

/**
 * Exported so its branches can be exercised with real props — no module mock,
 * no page render — the way the round-7 tests do.
 */
export function MatchSummaryPill({ matchSummary, matchingReached, onOpenMatches }: { matchSummary: MatchSummary | undefined; matchingReached: boolean; onOpenMatches: () => void }) {
  if (!matchSummary || matchSummary.run_status === 'not_applicable') return null

  // Badge defers to the workflow: matching runs decoupled from the linear
  // analysis workflow (it fires on `discoverable`), so a match count can exist
  // while the project still sits at an early stage. Until the Workflow Progress
  // bar actually reaches the Matching stage, don't assert a definitive overlap
  // count in the header — it would contradict the monotonic workflow strip. The
  // real count stays available on the Matches tab. See computeWorkflowStages.
  if (!matchingReached) {
    return (
      <span
        className={PILL_NEUTRAL}
        title="Matches appear here once the workflow reaches the Matching stage."
      >
        <PillLink />
        Matching pending
      </span>
    )
  }

  if (matchSummary.run_status === 'pending' || matchSummary.run_status === 'in_progress') {
    return (
      <span className={`${PILL_WARN} animate-pulse`}>Computing matches…</span>
    )
  }

  if (matchSummary.run_status === 'failed') {
    return (
      <span className={PILL_DANGER}>Match error</span>
    )
  }

  if (matchSummary.match_count === 0) {
    return (
      <span className={PILL_NEUTRAL}>
        <PillLink />
        No matches yet
      </span>
    )
  }

  return (
    <button
      onClick={onOpenMatches}
      // The figure draws this as `pill pill-neutral`, NOT in the brand blue
      // live used — the count is a statement of fact in the header, not a call
      // to action, and the row it sits in already carries the amber CTA. It
      // stays a button because it opens the Matches tab, which is real and
      // which the round-7 review scored; only its look comes from the figure.
      className={`${PILL_NEUTRAL} transition-colors hover:bg-[#E2E8F0]`}
      title="MatchSummaryPill — blind matches on topic overlap only; acting on one needs the review approved and both sides' consent"
    >
      <PillLink />
      {matchSummary.match_count} potential match{matchSummary.match_count !== 1 ? 'es' : ''}
    </button>
  )
}

export default function ProjectView() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const { user } = useAuth()

  // Folder management state
  const [showCreateFolder, setShowCreateFolder] = useState(false)
  const [createParentId, setCreateParentId] = useState<string | null>(null)
  const [newFolderName, setNewFolderName] = useState('')
  const [renameFolder, setRenameFolder] = useState<Folder | null>(null)
  const [renameName, setRenameName] = useState('')
  const [moveFolder, setMoveFolder] = useState<Folder | null>(null)
  const [moveTargetId, setMoveTargetId] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<Folder | null>(null)
  const [folderError, setFolderError] = useState('')
  const [showShareProject, setShowShareProject] = useState(false)
  const [permModalCollaborator, setPermModalCollaborator] = useState<{ grantee_id: string; grantee_email: string } | null>(null)
  const [isManager, setIsManager] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  // BUG-120: regenerate / full-reanalysis failure surfaced inline (was silent)
  const [regenError, setRegenError] = useState('')
  // Stream 9: Invention declaration
  const [researchSubTab, setResearchSubTab] = useState<'prior-art' | 'related-research'>('prior-art')
  const [activeTab, setActiveTab] = useState<ProjectTab>((searchParams.get('tab') as ProjectTab) || 'overview')
  // The Claims lens has its own four tabs (figure S1); every other tab on the
  // strip belongs to the invention page and navigating to it leaves the lens.
  const [lensTab, setLensTab] = useState<LensTab>('claims')
  const [matchPollTimedOut, setMatchPollTimedOut] = useState(false)
  const pollStartedAt = useRef<number | null>(null)
  // Bug #5895 (F15): the poll below already capped itself at 5 minutes, but on
  // expiry it just returned false and stopped — no flag was ever set, so the
  // panel's own "Analysing your project..." spinner had nothing telling it to
  // stop either, and kept spinning forever with no error or retry. Mirrors
  // matchPollTimedOut above, which already gets this right for match polling.
  const [analysisPollTimedOut, setAnalysisPollTimedOut] = useState(false)

  const analysisPollStartedAt = useRef<number | null>(null)
  const { data: projects, isLoading: projectsLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: () => getProjects(),
    // P3-9: while THIS project's analysis is pending/in_progress, poll so the
    // panel resolves to a result or a real failure instead of a stale spinner.
    // Bounded at 5 min (mirrors the match-summary poll) to avoid an endless loop.
    refetchInterval: (query) => {
      const proj = query.state.data?.find((p) => p.project_id === projectId)
      const status = proj?.ai_analysis_status
      if (status === 'pending' || status === 'in_progress') {
        if (!analysisPollStartedAt.current) analysisPollStartedAt.current = Date.now()
        if (Date.now() - analysisPollStartedAt.current > 5 * 60 * 1000) {
          setAnalysisPollTimedOut(true)
          return false
        }
        return 15000
      }
      analysisPollStartedAt.current = null
      setAnalysisPollTimedOut(false)
      return false
    },
  })
  // Reset the analysis-poll timeout when switching projects, same reasoning as
  // the match-poll reset below: this is component state that survives a
  // projectId change, so without this a stuck project's timeout would bleed
  // onto the next project's panel before its own poll has even started.
  useEffect(() => {
    setAnalysisPollTimedOut(false)
    analysisPollStartedAt.current = null
  }, [projectId])
  // T2b (#4552): when the projects poll (above) observes ai_analysis_status
  // transition from pending/in_progress to a terminal state, invalidate
  // project-analysis + assessment so the real result surfaces without a hard
  // refresh. Fires exactly once per transition (not on every 15s tick while
  // still in_progress) — the ref tracks the previous value across renders.
  const prevAnalysisStatusRef = useRef<string | undefined>(
    projects?.find((p) => p.project_id === projectId)?.ai_analysis_status,
  )
  useEffect(() => {
    const currentStatus = projects?.find((p) => p.project_id === projectId)?.ai_analysis_status
    const prevStatus = prevAnalysisStatusRef.current
    const wasInFlight = prevStatus === 'pending' || prevStatus === 'in_progress'
    const isTerminalNow = currentStatus !== 'pending' && currentStatus !== 'in_progress'
    if (wasInFlight && isTerminalNow) {
      queryClient.invalidateQueries({ queryKey: ['project-analysis', projectId] })
      queryClient.invalidateQueries({ queryKey: ['assessment', projectId] })
    }
    prevAnalysisStatusRef.current = currentStatus
  }, [projects, projectId, queryClient])
  // Fetch the single project by id as well: a cross-org collaborator granted access
  // via full disclosure (Stage 6) does NOT have this project in their own getProjects()
  // list, so without this its name fell back to the generic "Project".
  const { data: singleProject, isFetched: singleProjectFetched } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(projectId!),
    enabled: !!projectId,
    retry: false,
  })
  const project = projects?.find((p) => p.project_id === projectId) || singleProject

  // Bug #24: an aggregator opening a portfolio company's project via a grant
  // (not their own project) must see a read-only oversight view, never the
  // owner's data-entry copy. isOwner checks the project's real owner fields;
  // hasAggregatorAccess alone isn't a safe signal (an aggregator can also own
  // projects), so it's combined with !isOwner. Mirrors the read-only tone
  // SharedProjectView.tsx already uses for the disclosure-recipient case.
  const { hasAggregatorAccess } = useAggregator()
  const isOwner = !!user?.id && (project?.owner_id === user.id || (project?.owner_ids ?? []).includes(user.id))
  // Story #6757: a membership alone is not a portfolio grant; the server says how the caller got in.
  const viewingAsAggregator = hasAggregatorAccess && !!project && !isOwner && project.access_via !== 'collaborator'

  // Someone who can read an invention but does not own it (a disclosure recipient, a collaborator)
  // must get the read-only shared view, never the owner's page with owner controls. The server
  // already limits what they can do; this stops the page offering controls that would be refused.
  const isPlatformAdmin = (user?.groups ?? []).includes('innovateflow-admin')
  const sendToSharedView = shouldUseSharedView({
    projectId, hasProject: !!project, userId: user?.id, isOwner, hasAggregatorAccess, isPlatformAdmin,
    accessVia: project?.access_via,
  })
  useEffect(() => {
    if (sendToSharedView) navigate(`/shared/project/${projectId}`, { replace: true })
  }, [sendToSharedView, projectId, navigate])

  const { data: folders, isLoading: foldersLoading } = useQuery({
    queryKey: ['folders', projectId],
    queryFn: () => getFolders(projectId!),
    enabled: !!projectId,
  })

  // BUG-3221-FIX-S2D: pass includeOrphaned=true so the API returns s3_orphaned
  // docs alongside healthy ones; the local filter (showOrphans state) controls
  // visibility. Without this flag the API hides orphans server-side and the
  // "Show missing files" toggle would have nothing to show.
  const { data: documents, isLoading: docsLoading, error: docsError, refetch: refetchDocs } = useQuery({
    queryKey: ['documents', projectId, 'with-orphans'],
    queryFn: () => getDocuments(projectId!, undefined, true),
    enabled: !!projectId,
  })

  // Contract row S3's "Related in your set". The CACHE-ONLY read: it returns
  // an already-computed result and starts nothing, so opening one invention
  // can never trigger pair mathematics across the whole set. Never
  // getPortfolioOverlap here — that one enqueues on a miss by design.
  const { data: cachedOverlap } = useQuery({
    queryKey: ['cached-portfolio-overlap', 'own'],
    queryFn: () => getCachedPortfolioOverlap('own'),
    retry: false,
  })
  const projectNames = useMemo(
    () => Object.fromEntries((projects ?? []).map((p) => [p.project_id, p.name])),
    [projects],
  )

  // The Evidence lens IS the document page (contract row S2), so the switch
  // needs a document to open. Reuse the documents this page already fetches —
  // no extra request — and prefer the most recently analysed one, because a
  // document that has been through extraction is the one carrying claims to
  // read back. Falls back to the newest document, then to null (the switch
  // then renders its Evidence side disabled with an honest reason).
  const evidenceHref = useMemo(() => {
    const visible = (documents ?? []).filter((d) => !d.s3_orphaned)
    if (visible.length === 0) return null
    const byNewest = [...visible].sort(
      (a, b) => new Date(b.created_at ?? 0).getTime() - new Date(a.created_at ?? 0).getTime(),
    )
    const analysed = byNewest.find((d) => !!d.novelty_report)
    return `/document/${(analysed ?? byNewest[0]).document_id}`
  }, [documents])

  // Screen S1 — the Claims lens. The SAME query key the lens itself uses, so
  // TanStack serves both from one request: the lens renders the claims, the
  // page derives the next step and the "Claim 2 of 8" tail of the You're-at
  // line from them. There is no second source: the claim -> document edge is
  // re-filtered per reader on this route alone.
  const { data: projectClaims } = useQuery({
    queryKey: ['project-claims', projectId],
    queryFn: () => getProjectClaims(projectId!),
    enabled: !!projectId,
    retry: false,
  })

  // The lens and the focused claim both live in the URL, so a claim can be
  // linked to, and the back button walks the focus the way a reader expects.
  const inClaimsLens = searchParams.get('lens') === 'claims'
  // `?focus=claim:N` opens that claim; `?focus=none` is the reader having
  // closed the drawer; no param at all means "the lens picks one", because the
  // figure never shows this screen with an empty right column.
  const focusParam = searchParams.get('focus') ?? ''
  const focusMatch = /^claim:(\d+)$/.exec(focusParam)
  const focusClaim = focusMatch ? Number(focusMatch[1]) : null
  const focusClosed = focusParam === 'none'
  const setFocusClaim = useCallback((index: number | null) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      next.set('lens', 'claims')
      next.set('focus', index == null ? 'none' : `claim:${index}`)
      return next
    }, { replace: true })
  }, [setSearchParams])

  const { data: collaborators } = useQuery({
    queryKey: ['permissions', projectId],
    queryFn: () => getPermissions(projectId!),
    enabled: !!projectId,
  })

  // Tab count badges — preserved from the old flat tab bar so the primary tabs
  // still show how much lives behind them (Research Network = contacts + orgs,
  // Notes, Access = collaborators).
  const { data: tabContactsData } = useQuery({
    queryKey: ['project-list', projectId, 'contact'],
    queryFn: () => countOrNone(() => getFolderListItems(projectId!, projectId!, 'contact'), { items: [], count: 0 }),
    enabled: !!projectId,
    retry: noRetryOn404,
  })
  const { data: tabOrgsData } = useQuery({
    queryKey: ['project-list', projectId, 'organisation'],
    queryFn: () => countOrNone(() => getFolderListItems(projectId!, projectId!, 'organisation'), { items: [], count: 0 }),
    enabled: !!projectId,
    retry: noRetryOn404,
  })
  const { data: tabNotesData } = useQuery({
    queryKey: ['project-notes', projectId],
    queryFn: () => countOrNone(() => getFolderNotes(projectId!, projectId!), { notes: [], count: 0 }),
    enabled: !!projectId,
    retry: noRetryOn404,
  })

  // Fetch project owners
  const { data: owners, refetch: refetchOwners } = useQuery({
    queryKey: ['project-owners', projectId],
    queryFn: () => getProjectOwners(projectId!),
    enabled: !!projectId,
  })

  // AI Analysis — check for stale summary
  const { data: projectAnalysis } = useQuery({
    queryKey: ['project-analysis', projectId],
    queryFn: () => getProjectAnalysis(projectId!),
    enabled: !!projectId,
  })
  // Prior art data — used to deduplicate journals
  const { data: priorArt } = useQuery({
    queryKey: ['project-prior-art', projectId],
    queryFn: () => getProjectPriorArt(projectId!),
    enabled: !!projectId,
  })
  // Assessment scores — used by the project-level Workflow Progress strip to
  // light up the Assessment stage. Tolerates either response shape (the API
  // returns both `scores.*` and `assessment.axes.*` depending on whether the
  // pipeline has run; both are valid signals that scoring has produced output).
  // Bug #6930: once the analysis exists but the scores do not, the scores are on
  // their way (the assessment runs on its own after the analysis). Re-check every
  // 10 s, for at most 10 minutes, so "Assessment: in progress" resolves to Review
  // without a hard reload. Stops as soon as scores or an honest "insufficient
  // basis" answer arrive.
  const assessmentPollStartedAt = useRef<number | null>(null)
  const analysisExistsRef = useRef(false)
  const { data: assessmentData } = useQuery({
    queryKey: ['assessment', projectId],
    queryFn: () => getAssessment(projectId!),
    enabled: !!projectId,
    retry: 1,
    refetchInterval: (query) => {
      const kind = deriveAssessmentState(query.state.data).kind
      if (kind === 'scored' || kind === 'insufficient_basis' || !analysisExistsRef.current) {
        assessmentPollStartedAt.current = null
        return false
      }
      if (!assessmentPollStartedAt.current) assessmentPollStartedAt.current = Date.now()
      return Date.now() - assessmentPollStartedAt.current > 10 * 60 * 1000 ? false : 10000
    },
  })
  // Demo UX findings (22 Aug 2026), item 1: the stepper's "has scores" check and
  // the Overview panel's score parser used to be two DIFFERENT pieces of logic —
  // the stepper lit Assessment green whenever the axes object merely had keys,
  // while the panel's parser missed the live backend's axis names
  // (novelty/feasibility/market_potential) and rendered "Not assessed" ×3 on the
  // same screen. Both now derive from the ONE shared normaliser, so the stepper
  // marks Assessment complete exactly when the panel can show the scores.
  const assessmentState = deriveAssessmentState(assessmentData)
  const assessmentScores = assessmentState.kind === 'scored' ? assessmentState.scores : undefined
  const hasAssessmentScores = assessmentScores != null
  const isInsufficientBasis = assessmentState.kind === 'insufficient_basis'
  // Bug #5297: the header's "NEXT: RUN ANALYSIS" banner and this Overview
  // panel's own Assessment placeholder used to read ONLY the project-level
  // summary field, while the Claims lens (Figure 2) reads per-document
  // novelty_report.claim_analysis straight off THIS SAME `documents` query
  // ('documents', projectId, 'with-orphans' — already fetched above for the
  // Documents tab, no new network call). A project can have completed,
  // fully-scored claims on its documents while the separate project-level
  // summary field is still empty — that combination is exactly what the demo
  // project showed: 10 scored claims, but the banner and stepper both said
  // analysis hadn't run. Single source of truth per the bug's own ask: "has
  // analysis run" is now true whenever EITHER the project-level summary
  // exists OR any document already carries a completed claim analysis — the
  // same data the Claims lens itself renders.
  const hasProjectAnalysis = !!(projectAnalysis && (
    (projectAnalysis as unknown as { summary?: string }).summary ||
    (projectAnalysis as unknown as { ai_analysis?: { summary?: string } }).ai_analysis?.summary
  )) || (documents || []).some(
    (d) => d.novelty_report?.status === 'completed' && (d.novelty_report?.claim_analysis?.length ?? 0) > 0,
  )
  analysisExistsRef.current = hasProjectAnalysis
  const { data: matchSummary } = useQuery<MatchSummary>({
    queryKey: ['matchSummary', projectId],
    queryFn: () => getMatchSummary(projectId!),
    // ROUND 7, Addendum 7. This `enabled` was the REAL gate: with Discoverable
    // off the summary was never fetched, so removing the guard on the pill
    // alone would have changed nothing on the live screen. Discoverable governs
    // whether OTHERS can find this invention; the owner may always see the
    // matches already found for it, so the owner's own view fetches
    // unconditionally.
    //
    // An aggregator reading someone else's invention through a portfolio grant
    // keeps the OLD condition exactly. Addendum 7 is about the owner, and
    // widening what a third party sees is not a change anyone asked for.
    enabled: !!projectId && !!project && (!viewingAsAggregator || project.discoverable === true),
    refetchInterval: (query) => {
      const status = query.state.data?.run_status
      if (status === 'pending' || status === 'in_progress') {
        if (!pollStartedAt.current) pollStartedAt.current = Date.now()
        if (Date.now() - pollStartedAt.current > 5 * 60 * 1000) {
          setMatchPollTimedOut(true)
          return false
        }
        return 15000
      }
      pollStartedAt.current = null
      setMatchPollTimedOut(false)
      return false
    },
  })
  // Reset the match-poll timeout when switching projects. matchPollTimedOut is
  // component state that survives a projectId change (the route reuses this
  // component), so without this a genuinely-stuck project's timeout flag would
  // bleed onto the next project and show a false "taking longer than expected"
  // screen on a project whose matching never even started (T2).
  useEffect(() => {
    setMatchPollTimedOut(false)
    pollStartedAt.current = null
  }, [projectId])
  // Matching stage = the project is set to Discoverable. The backend
  // synchronously syncs cross-pollination consent when this flag flips so
  // Discoverable=true means the project is in the matching pool.
  const hasProjectMatching = !!project?.discoverable
  const { data: disclosureAgreements = [] } = useQuery<DisclosureAgreement[]>({
    queryKey: ['disclosureAgreements', projectId],
    queryFn: () => getDisclosureAgreements(projectId!),
    enabled: !!projectId && hasProjectMatching && matchSummary?.run_status === 'complete',
  })
  const disclosureComplete = disclosureAgreements.some((a) => a.current_level >= 4)
  // 4e: A disclosure agreement that reaches Level 6 (Full Collaboration) grants
  // bidirectional access at the disclosure layer, but never writes a row into the
  // permissions table that getPermissions() reads — so the Access tab showed
  // "No collaborators yet" even after Full Collaboration. Surface the counterparty
  // here directly from the agreement (the single source of truth — it already
  // carries the counterparty identity at L4+), deduped against real permission grants.
  const disclosureCollaborators = useMemo(() => disclosureAgreements
    .filter((a) => (a.current_level ?? 0) >= 6 && (a.counterparty_org_id || a.counterparty_email))
    .map((a) => ({
      grantee_id: a.counterparty_org_id || a.counterparty_email || a.agreement_id,
      grantee_email: a.counterparty_name || a.counterparty_email || 'Collaborating organisation',
      matchEmail: (a.counterparty_email || '').toLowerCase(),
      permission_level: 'full collaboration',
      fromDisclosure: true as const,
    })), [disclosureAgreements])
  const displayCollaborators = useMemo(() => {
    const permEmails = new Set((collaborators || []).map((p) => (p.grantee_email || '').toLowerCase()).filter(Boolean))
    const extras = disclosureCollaborators.filter((d) => !d.matchEmail || !permEmails.has(d.matchEmail))
    return [...(collaborators || []), ...extras]
  }, [collaborators, disclosureCollaborators])
  const priorArtPaperIds = useMemo(() => new Set((priorArt?.related_papers || []).map((p) => p.paper_id)), [priorArt])
  const priorArtPaperTitles = useMemo(() => new Set((priorArt?.related_papers || []).map((p) => (p.title || '').toLowerCase())), [priorArt])
  // BUG-141: the AI-analysis pill drill-down filters the project's documents and
  // prior-art papers by term. Map the fetched prior-art summary's related_papers
  // into the JournalArticle-ish shape the panel/drill-down expects ({_id, title})
  // so the panel is no longer mounted with empty defaults (which made every pill
  // drill-down render a permanently-empty result).
  const priorArtArticles = useMemo(
    () => (priorArt?.related_papers || []).map((p) => ({ _id: p.paper_id, title: p.title })),
    [priorArt],
  )

  // BUG-135: never render raw ciphertext as the invention declaration. The
  // backend fail-closes by REMOVING the field and stamping _decrypt_error
  // (see utils/encryption.ts), but legacy rows / missing-DEK items can still
  // carry a base64 blob in the field itself — both cases get a friendly
  // "cannot display" state instead of the green declaration card.
  const declarationUnreadable =
    fieldDecryptFailed(project, 'invention_declaration') ||
    looksLikeCiphertext(project?.invention_declaration)

  // Story #6766: the problem and approach are encrypted the same way, so the
  // intake card says Locked for them when the server withheld them.
  const intakeUnreadableFields = (['problem', 'approach'] as const).filter((f) =>
    fieldDecryptFailed(project, `invention_${f}`),
  )

  const handleFullReanalysis = async () => {
    if (!projectId) return
    setRegenerating(true)
    setRegenError('')
    try {
      await reprocessProject(projectId)
      queryClient.invalidateQueries({ queryKey: ['project-analysis', projectId] })
      // T2b (#4552): the assessment is scored from the analysis, so it is stale
      // the moment a re-run starts.
      queryClient.invalidateQueries({ queryKey: ['assessment', projectId] })
    } catch (err) {
      // BUG-120: surface the failure rather than swallowing it.
      const e = err as Error & { response?: { data?: { error?: string } } }
      setRegenError(e.response?.data?.error || e.message || 'Failed to run the full re-analysis. Please try again.')
      // T2b (#4552): same >29s/504 recovery as handleRegenerateAnalysis.
      queryClient.invalidateQueries({ queryKey: ['projects'] })
    } finally {
      setRegenerating(false)
    }
  }

  // Owner management state
  const [showAddOwner, setShowAddOwner] = useState(false)
  const [newOwnerEmail, setNewOwnerEmail] = useState('')
  const [ownerError, setOwnerError] = useState('')

  const addOwnerMutation = useMutation({
    mutationFn: (email: string) => addProjectOwner(projectId!, email),
    onSuccess: () => { refetchOwners(); setShowAddOwner(false); setNewOwnerEmail(''); setOwnerError('') },
    onError: (err: Error & { response?: { data?: { error?: string } } }) =>
      setOwnerError(err.response?.data?.error || 'Failed to add owner'),
  })

  const removeOwnerMutation = useMutation({
    mutationFn: (userId: string) => removeProjectOwner(projectId!, userId),
    onSuccess: () => refetchOwners(),
    onError: (err: Error & { response?: { data?: { error?: string } } }) =>
      setOwnerError(err.response?.data?.error || 'Failed to remove owner'),
  })

  // Check if current user has manager-level access
  useEffect(() => {
    if (projectId && user?.id) {
      checkManagerAccess(projectId, user.id).then(setIsManager)
    }
  }, [projectId, user?.id])

  const invalidateFolders = () => queryClient.invalidateQueries({ queryKey: ['folders', projectId] })

  const createFolderMutation = useMutation({
    mutationFn: (data: { name: string; parent_folder_id: string | null }) => createFolder(projectId!, data),
    onSuccess: () => {
      invalidateFolders()
      setShowCreateFolder(false)
      setNewFolderName('')
      setCreateParentId(null)
      setFolderError('')
    },
    onError: (err: Error & { response?: { data?: { error?: string } } }) => {
      setFolderError(err.response?.data?.error || err.message || 'Failed to create folder')
    },
  })

  const renameFolderMutation = useMutation({
    mutationFn: ({ folderId, name }: { folderId: string; name: string }) =>
      updateFolder(projectId!, folderId, { name }),
    onSuccess: () => {
      invalidateFolders()
      setRenameFolder(null)
      setRenameName('')
      setFolderError('')
    },
    onError: (err: Error & { response?: { data?: { error?: string } } }) => {
      setFolderError(err.response?.data?.error || err.message || 'Failed to rename folder')
    },
  })

  const moveFolderMutation = useMutation({
    mutationFn: ({ folderId, parentId }: { folderId: string; parentId: string | null }) =>
      updateFolder(projectId!, folderId, { parent_folder_id: parentId }),
    onSuccess: () => {
      invalidateFolders()
      setMoveFolder(null)
      setMoveTargetId(null)
      setFolderError('')
    },
    onError: (err: Error & { response?: { data?: { error?: string } } }) => {
      setFolderError(err.response?.data?.error || err.message || 'Failed to move folder')
    },
  })

  const deleteFolderMutation = useMutation({
    mutationFn: (folderId: string) => deleteFolder(projectId!, folderId),
    onSuccess: () => {
      invalidateFolders()
      setDeleteTarget(null)
      setFolderError('')
    },
    onError: (err: Error & { response?: { data?: { error?: string } } }) => {
      setFolderError(err.response?.data?.error || err.message || 'Failed to delete folder')
    },
  })

  const removeCollaboratorMutation = useMutation({
    mutationFn: (granteeId: string) => revokePermission(projectId!, granteeId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['permissions', projectId] }),
  })

  // Story #3477 Stream 9 (#3476): discoverable toggle matching Dashboard.tsx
  const toggleDiscoverableMutation = useMutation({
    mutationFn: ({ id, discoverable }: { id: string; discoverable: boolean }) =>
      updateProject(id, { discoverable }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
    },
  })

  const approveReviewMutation = useMutation({
    mutationFn: (id: string) => approveProjectReview(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      // Demo UX findings (22 Aug 2026), item 3: approving the review only flips
      // review_status — it does NOT compute anything. But without refetching the
      // assessment here, a stale "no scores" cache entry could keep the Overview
      // on "Not assessed" until some later action forced a refresh, which made
      // the approve click LOOK like it had conjured the scores. Refetch both so
      // the whole page reflects the real post-approval state immediately.
      queryClient.invalidateQueries({ queryKey: ['assessment', id] })
    },
  })

  // Inline file upload state
  interface InlineUpload { file: File; status: 'pending' | 'uploading' | 'complete' | 'error'; progress: number; error?: string }
  const [inlineFiles, setInlineFiles] = useState<InlineUpload[]>([])
  const [dragOver, setDragOver] = useState(false)
  // STORY-DOC-SEARCH (Round 6): within-project document filter. The
  // documents list previously rendered every document with no filter,
  // so projects past ~30 documents required scrolling through everything
  // to find one. The filter matches case-insensitively against title and
  // also tags / topics so users can find by subject.
  const [docFilter, setDocFilter] = useState('')
  const [docView, setDocView] = useState<'list' | 'grid'>(() => {
    try { return (localStorage.getItem('innovateflow:doc-view:project') as 'list' | 'grid') || 'list' } catch { return 'list' }
  })
  // BUG-3221-FIX-S2D: hide s3_orphaned docs by default; toggle to surface them
  // when the inventor needs to identify/recover missing files.
  const [showOrphans, setShowOrphans] = useState(false)

  const inlineUploadMutation = useMutation({
    mutationFn: async (uf: InlineUpload) => {
      const contentType = uf.file.type || 'application/octet-stream'
      const { upload_url, document_id, s3_key } = await getUploadUrl(projectId!, uf.file.name, contentType)
      await axios.put(upload_url, uf.file, {
        headers: { 'Content-Type': contentType },
        onUploadProgress: (e) => {
          if (e.total) {
            const progress = Math.round((e.loaded / e.total) * 100)
            setInlineFiles((prev) => prev.map((f) => (f.file === uf.file ? { ...f, progress } : f)))
          }
        },
      })
      await confirmUpload({ document_id, project_id: projectId!, s3_key, title: uf.file.name, filename: uf.file.name, content_type: contentType })
      return document_id
    },
    onSuccess: (_docId, uf) => {
      setInlineFiles((prev) => prev.map((f) => (f.file === uf.file ? { ...f, status: 'complete', progress: 100 } : f)))
      queryClient.invalidateQueries({ queryKey: ['documents', projectId] })
    },
    onError: (err, uf) => {
      setInlineFiles((prev) => prev.map((f) => (f.file === uf.file ? { ...f, status: 'error', error: err instanceof Error ? err.message : 'Upload failed' } : f)))
    },
  })

  const handleInlineDrop = useCallback((e: DragEvent) => {
    e.preventDefault()
    setDragOver(false)
    if (!e.dataTransfer.files.length) return
    const newFiles: InlineUpload[] = Array.from(e.dataTransfer.files).map((file) => ({ file, status: 'uploading' as const, progress: 0 }))
    setInlineFiles((prev) => [...prev, ...newFiles])
    newFiles.forEach((uf) => inlineUploadMutation.mutate(uf))
  }, [projectId, inlineUploadMutation])

  const handleInlineFileInput = useCallback((fileList: FileList) => {
    const newFiles: InlineUpload[] = Array.from(fileList).map((file) => ({ file, status: 'uploading' as const, progress: 0 }))
    setInlineFiles((prev) => [...prev, ...newFiles])
    newFiles.forEach((uf) => inlineUploadMutation.mutate(uf))
  }, [inlineUploadMutation])

  const handleFolderAction = (action: FolderAction) => {
    setFolderError('')
    switch (action.type) {
      case 'create':
        setCreateParentId(action.parentFolderId ?? null)
        setNewFolderName('')
        setShowCreateFolder(true)
        break
      case 'rename':
        if (action.folder) {
          setRenameFolder(action.folder)
          setRenameName(action.folder.name)
        }
        break
      case 'move':
        if (action.folder) {
          setMoveFolder(action.folder)
          setMoveTargetId(action.folder.parent_folder_id)
        }
        break
      case 'delete':
        if (action.folder) setDeleteTarget(action.folder)
        break
    }
  }

  // Get eligible move targets (exclude the folder being moved and its descendants)
  const getMoveTargets = (folder: Folder): Folder[] => {
    if (!folders) return []
    const descendants = new Set<string>()
    const collectDescendants = (fid: string) => {
      descendants.add(fid)
      folders.filter((f) => f.parent_folder_id === fid).forEach((f) => collectDescendants(f.folder_id))
    }
    collectDescendants(folder.folder_id)
    return folders.filter((f) => !descendants.has(f.folder_id))
  }

  // Single source of truth for workflow position, shared by the Workflow Progress
  // strip and the header overlap badge so they can never contradict each other.
  // BUG: this used to require a NON-orphaned document (`!d.s3_orphaned`), while
  // the Projects list derives the same "Upload" stage from `document_count > 0`
  // (src/utils/projectStage.ts → deriveProjectWorkflowStages, which counts every
  // uploaded document regardless of later S3 orphaning). The two disagreed for
  // any project whose only document(s) later became orphaned — e.g. "ZTA 2.5"
  // showed "Intake" on the Projects list but "Upload" on its own Overview page.
  // computeWorkflowStages' own doc comment says Upload "← documents exist for
  // the project", not "← a retrievable document exists" — an upload is a
  // historical fact that a later S3 file loss shouldn't undo. Match the list
  // page's semantics so the two views can never contradict each other again.
  const workflowStages = computeWorkflowStages({
    hasDeclaration: !!project?.invention_declaration,
    hasDocuments: (documents || []).length > 0,
    hasAnalysis: hasProjectAnalysis,
    hasScores: hasAssessmentScores,
    hasReviewApproved: project?.review_status === 'approved',
    hasMatching: hasProjectMatching,
    matchCount: matchSummary?.match_count ?? 0,
    runStatus: matchSummary?.run_status ?? '',
    disclosureComplete,
  })
  const matchingReached = hasReachedStage(workflowStages, 'Matching')

  // ── The spine (screens S1 and S3) ──────────────────────────────────────────
  // The page states where the reader is; the shell renders the rail, the
  // You're-at line and the single crumb from it. Every value is live: the
  // invention's real name, the real claim index and total, and a step count
  // taken from the pipeline (or, in the Claims lens, from the claims).
  const activeStage = workflowStages.find((s) => s.status === 'active')
  const claimRows = projectClaims?.claims ?? []

  // The next step the CLAIMS lens shows. It is a different question from the
  // pipeline's — "which claim needs me", not "which stage is this invention
  // at" — so it is derived from the claims and nothing else. A combination
  // claim that scores as novel is the one worth protecting, because no single
  // document asserts it; failing that, a claim with nothing behind it is the
  // filing risk that only a claim-shaped view can see.
  const claimsNextStep = (() => {
    const combination = claimRows
      .map((claim, i) => ({ claim, number: i + 1 }))
      .filter((row) => row.claim.supporting_document_ids.length > 1 && (row.claim.novelty_score ?? 0) >= 60)
      .sort((a, b) => (b.claim.novelty_score ?? 0) - (a.claim.novelty_score ?? 0))[0]
    if (combination) {
      return {
        verb: 'protect',
        icon: '🛡',
        headline: `Claim ${combination.number} is novel and no single document asserts it — consider it for protection.`,
        rule: "from the claim analysis across this invention's disclosure",
        cta: { label: 'See IP suggestions →', tab: 'next-steps' as ProjectTab },
      }
    }
    const unsupported = claimRows.findIndex((c) => c.supporting_document_ids.length === 0)
    if (unsupported >= 0) {
      return {
        // NOT 'evidence': the Evidence lens pill sits a few pixels above this
        // eyebrow, and the same word in both places reads as a link between
        // them that does not exist. The figure only draws the PROTECT case, so
        // this one takes the plainest verb for what the sentence asks.
        verb: 'support the claim',
        icon: '⚑',
        headline: `Claim ${unsupported + 1} is asserted with no supporting document — add the document that carries it, or drop the claim.`,
        rule: "from the claim analysis across this invention's disclosure",
        cta: { label: 'Add a document →', to: uploadLinkFor(projectId) },
      }
    }
    return null
  })()

  const inventionName = project?.name ?? ''
  // The claim the drawer is open on: the URL's, or — because the figure never
  // shows this screen without one — the default the lens picks. One rule, one
  // helper, so the rail, the crumb and the drawer always name the same claim.
  const focusedIndex = focusClosed ? null : resolveFocusIndex(claimRows, focusClaim)
  const focusedClaim = focusedIndex ? claimRows[focusedIndex - 1] : null
  const claimDocumentHref = focusedClaim?.supporting_document_ids[0]
    ? `/document/${focusedClaim.supporting_document_ids[0]}`
    : evidenceHref ?? undefined

  useSetSpine(project ? {
    level: 'invention',
    // Claim and Document are FOCUSES inside a lens, not scopes of their own, so
    // the rail names two notches here and the figures show two.
    levels: viewingAsAggregator ? ['invention', 'entity', 'portfolio', 'across'] : ['invention', 'my-inventions'],
    links: { 'my-inventions': '/projects' },
    youreAt: inClaimsLens
      ? [inventionName, { b: 'Claims', text: 'lens' }, ...(focusedClaim ? [`Claim ${focusedIndex} of ${claimRows.length}`] : [])]
      : [inventionName],
    stepsWaiting: inClaimsLens ? (claimsNextStep ? 1 : 0) : (activeStage ? 1 : 0),
    zoomIn: inClaimsLens
      ? { title: 'Zoom in — this is the closest view' }
      : { title: 'Invention is the innermost scope — a claim or a document is a focus inside a lens, not a scope of its own' },
    zoomOut: inClaimsLens
      ? { to: claimDocumentHref, title: 'Zoom out — Document' }
      : { to: '/projects', title: 'Zoom out — My inventions' },
    crumb: [
      { label: viewingAsAggregator ? 'Portfolio' : 'Personal', to: '/' },
      { label: 'My inventions', to: '/projects' },
      inClaimsLens ? { label: inventionName, to: `/project/${projectId}` } : inventionName,
      ...(inClaimsLens
        ? [focusedClaim
            ? { label: 'Claims', to: `/project/${projectId}?lens=claims` }
            : 'Claims']
        : []),
      ...(inClaimsLens && focusedClaim ? [`Claim ${focusedIndex}`] : []),
    ],
  } : null)

  // Hard access gate: a user who is not a member of this project — it is neither
  // in their own getProjects() list NOR returned by a direct getProject() fetch
  // (which a cross-org disclosure collaborator WOULD receive) — must never see the
  // project shell. While either source query is still resolving, show the skeleton;
  // once both have settled with no project, render a no-access state instead of
  // leaking the layout, upload control, and tab chrome with a generic "Project" title.
  // ── The two header actions figure S1 draws and the build did not have ────
  //
  // Declared HERE, above the page's access gate, because they are hooks: a
  // component that returns early on a loading or no-access render must still
  // call every hook it ever calls, in the same order.
  //
  // "Download" at invention level means every document of this invention. It
  // uses the same per-document presigned-URL route the document page uses, one
  // request per file, so a reader downloads exactly what they are entitled to
  // and nothing new had to be built on the server. No archive is assembled:
  // the browser saves the real files under their real names. The documents are
  // passed IN rather than read from a later const, so this never depends on a
  // line the early return may not have reached.
  // The intake card draws no control of its own — the figure has none — so the
  // page owns whether its editor is open and offers it from ⋯ More.
  const [intakeEditing, setIntakeEditing] = useState(false)
  const [inventionDownloadError, setInventionDownloadError] = useState('')
  const downloadInventionMutation = useMutation({
    mutationFn: async (docs: InventionDocument[]) => {
      if (docs.length === 0) throw new Error('This invention has no documents to download yet.')
      for (const doc of docs) {
        const url = await downloadDocument(doc.document_id)
        const link = window.document.createElement('a')
        link.href = url
        link.rel = 'noopener'
        link.download = doc.filename || doc.title || doc.name || 'document'
        window.document.body.appendChild(link)
        link.click()
        window.document.body.removeChild(link)
        // A short gap between saves: browsers throttle a burst of programmatic
        // downloads and silently drop the ones that arrive too close together.
        await new Promise((resolve) => { window.setTimeout(resolve, 350) })
      }
      return docs.length
    },
    onSuccess: () => setInventionDownloadError(''),
    onError: (e: Error) => setInventionDownloadError(e?.message || 'The download did not start. Try again.'),
  })

  // "Mark as Reference" at invention level: this whole invention is background
  // in your set rather than something you are pursuing. Same meaning as the
  // flag on a document, one level up, and it is a plain field on the project
  // record — nothing is hidden, nothing is deleted, and it changes no
  // permission. The header mirrors the saved state so the button never
  // disagrees with the record.
  const referenceMutation = useMutation({
    mutationFn: (next: boolean) => updateProject(projectId!, { is_reference: next } as Partial<Project>),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['project', projectId] })
      queryClient.invalidateQueries({ queryKey: ['projects'] })
    },
  })

  if (!project && (projectsLoading || !singleProjectFetched)) return <LoadingSkeleton rows={4} />
  if (!project) {
    return (
      <div className="max-w-lg mx-auto mt-16 text-center">
        <AlertTriangle size={40} className="mx-auto text-amber-500 mb-4" />
        <h1 className="text-xl font-bold text-gray-900 mb-2">Project not available</h1>
        <p className="text-sm text-gray-600 mb-6">
          This project doesn’t exist, or you don’t have access to it. If you believe you should
          have access, ask the project owner to share it with you.
        </p>
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-2 px-4 py-2 bg-iflow-700 text-white text-sm font-medium rounded-lg hover:bg-iflow-800 transition-colors"
        >
          <Home size={16} /> Back to Projects
        </button>
      </div>
    )
  }



  const reviewStageStatus = workflowStages.find((s) => s.name === 'Review')?.status

  // ── Progressive-disclosure tab model (P2) ──
  const PROJECT_TABS: TabItem<ProjectTab>[] = [
    { key: 'overview', label: 'Overview', icon: LayoutDashboard },
    { key: 'ai-analysis', label: TAB_LABELS['ai-analysis'], icon: Sparkles },
    { key: 'documents', label: 'Documents', icon: FileText, count: (documents ?? []).length },
    { key: 'research', label: TAB_LABELS.research, icon: Search },
    { key: 'matches', label: 'Matches', icon: Shuffle },
    { key: 'whitespace', label: 'White Space', icon: Compass },
    { key: 'market', label: TAB_LABELS.market, icon: BarChart3 },
    { key: 'collaboration', label: 'Research Network', icon: Network, count: (tabContactsData?.count ?? 0) + (tabOrgsData?.count ?? 0) },
    { key: 'next-steps', label: TAB_LABELS['next-steps'], icon: Zap },
    { key: 'review', label: 'Review', icon: ClipboardCheck, pending: reviewStageStatus === 'active' },
    { key: 'connections', label: 'Connections', icon: GitBranch },
    { key: 'notes', label: 'Notes', icon: StickyNote, count: tabNotesData?.count ?? 0 },
    { key: 'shared', label: 'Access', icon: Share2, count: collaborators?.length ?? 0 },
    { key: 'topics', label: 'Topics', icon: Tag },
    { key: 'reference-components', label: 'Reference Library', icon: Puzzle },
    { key: 'activity', label: 'Activity', icon: Clock },
    { key: 'aggregators', label: 'Aggregators', icon: Users2 },
  ]
  // #4937 RED-TEAM amendment G: White Space promoted into PRIMARY (not "More")
  // — the mockups show it visible on every project screen; overflow placement
  // would score MISS at the visual-diff acceptance gate (Task 2d).
  // UX-020: the stepper's Review step opens the Review tab, so that tab must be
  // on the visible strip while the stage is active or still ahead; it drops back
  // behind More once the review is approved (then it is history, not a task).
  const PROJECT_PRIMARY: ProjectTab[] = [
    'overview', 'ai-analysis', 'documents', 'research', 'matches', 'whitespace', 'market', 'collaboration',
    ...(reviewTabIsPrimary(reviewStageStatus) ? (['review'] as ProjectTab[]) : []),
  ]

  // BUG: `?tab=crm` (and the older `research-network` key) rendered a
  // completely blank tab body — 'crm'/'research-network' are not members of
  // the ProjectTab union, so the switch below matched no case and
  // ProgressiveTabBar highlighted nothing either. The project's actual CRM
  // content lives on the 'collaboration' tab (labelled "Research Network"),
  // so alias the legacy values there; anything else unrecognised falls back
  // to 'overview' instead of rendering nothing.
  // Story #5305 Stream 14b (Task #5318): the portfolio Overlap "raise with
  // the leads" notification links here with `?tab=overlap` (design §6.4 —
  // "the notification lands on the lead's own project"). There is no
  // project-scoped overlap tab (overlap is a PORTFOLIO concept — the
  // recipient's own payload never names the other project, so there is
  // nothing group-specific to render at this scope); the closest real
  // surface is this project's own White Space tab, so the alias lands the
  // recipient somewhere real instead of silently falling through to
  // 'overview' with no explanation of why they arrived at all.
  const TAB_ALIASES: Partial<Record<string, ProjectTab>> = {
    crm: 'collaboration', 'research-network': 'collaboration', overlap: 'whitespace',
  }
  const knownTabKeys = new Set<string>(PROJECT_TABS.map((t) => t.key))
  const resolvedActiveTab: ProjectTab = knownTabKeys.has(activeTab) ? activeTab : (TAB_ALIASES[activeTab] ?? 'overview')
  // The RAW (pre-alias) tab param — used only to show the notify-landing
  // banner exactly once, on the actual inbound link, never on an ordinary
  // visit to White Space.
  const landedFromOverlapNotify = searchParams.get('tab') === 'overlap'

  // ── The one next step at this scope (figures S1 and S3) ───────────────────
  // Derived from the same workflow-stage model the progress strip uses, so the
  // banner can never contradict it. The first still-incomplete stage IS the
  // next action; every CTA maps to a real handler that already exists on this
  // page. The verb under "NEXT AT INVENTION" names what the reader is being
  // asked to DO, which is the whole grammar of the banner.
  interface PageNextStep {
    verb: string
    icon: string
    headline: string
    rule: string
    cta?: { label: string; onClick?: () => void; to?: string }
    /** Overrides the eyebrow's "where" segment. The figures do not use one
     * grammar for every step: S3 draws "Next at Invention · Claims lens ·
     * approve" because the checking happens in the claims lens, while S1 draws
     * two segments. Each step says where it is done, in the figure's words. */
    at?: string
  }
  const tabCta = (label: string, tab: ProjectTab) =>
    // Bug (Roger, 24 Aug): a CTA that just switches to the tab you are already
    // on fires with no visible effect and reads as a dead button.
    resolvedActiveTab === tab ? undefined : { label, onClick: () => setActiveTab(tab) }

  let nextStep: PageNextStep | null = null
  if (viewingAsAggregator) {
    // Bug #24: read-only oversight — never the owner's data-entry CTAs.
    nextStep = {
      verb: 'read',
      icon: '👁',
      headline: 'You are viewing this invention through a portfolio grant, not as its owner.',
      rule: 'read-only — nothing you do here changes the inventor’s record',
      cta:
        activeStage?.name === 'Analysis' || activeStage?.name === 'Assessment'
          ? tabCta('Open AI Analysis', 'ai-analysis')
          : project?.discoverable
            ? tabCta('View matches', 'matches')
            : undefined,
    }
  } else if (!activeStage) {
    nextStep = {
      verb: 'nothing waiting',
      icon: '✔',
      headline: 'This invention has moved through the full pipeline.',
      rule: 'from the pipeline',
      cta: tabCta('View matches', 'matches'),
    }
  } else {
    switch (activeStage.name) {
      case 'Upload':
        nextStep = {
          verb: 'upload',
          icon: '⬆',
          headline: 'Add a document to get started — analysis begins the moment you upload.',
          rule: 'from the pipeline',
          cta: { label: 'Upload a document', onClick: () => navigate(uploadLinkFor(projectId)) },
        }
        break
      case 'Intake':
        nextStep = {
          verb: 'describe your invention',
          icon: '🧾',
          // A declaration that exists but cannot be decrypted is not missing: telling
          // the user to "add" one contradicts the locked intake card (UX-022).
          headline: declarationUnreadable
            ? 'Your invention declaration is locked: check your key access so the analysis can anchor to it.'
            : 'Add a one-line invention declaration so the analysis can anchor to your core concept.',
          rule: 'from the pipeline',
          cta: tabCta('Open the intake', 'overview'),
        }
        break
      case 'Analysis':
        nextStep = {
          verb: 'analyse',
          icon: '🧠',
          headline: 'Run the analysis to generate this invention’s summary, themes and novel aspects.',
          rule: 'from the pipeline',
          cta: tabCta('Open AI Analysis', 'ai-analysis'),
        }
        break
      case 'Assessment':
        // Demo UX findings (22 Aug 2026), item 2: Analysis and Assessment are
        // different stages, and each line talks about its OWN step.
        nextStep = {
          verb: 'wait for the scores',
          icon: '📊',
          headline: 'The assessment is scoring your completed analysis. The scores appear on this page when they are ready.',
          rule: 'from the pipeline · this runs on its own',
          cta: tabCta('Open AI Analysis', 'ai-analysis'),
        }
        break
      case 'Review':
        // The approve control is owner-only on the backend, so a read-only
        // viewer gets the sentence and never a button that 403s on click.
        nextStep = canApproveReview(isOwner, hasAssessmentScores, project?.review_status)
          ? {
              verb: 'approve',
              at: 'Invention · Claims lens',
              icon: '✔',
              headline: 'Your assessment scores are in — check them and the analysis, then approve to unlock matching.',
              rule: 'from the pipeline',
              cta: {
                label: approveReviewMutation.isPending ? 'Approving…' : 'Approve & unlock matching',
                onClick: () => projectId && approveReviewMutation.mutate(projectId),
              },
            }
          : {
              verb: 'approve',
              at: 'Invention · Claims lens',
              icon: '✔',
              headline: 'The assessment scores are in. Only an owner of this invention can approve the review and unlock matching.',
              rule: 'from the pipeline',
            }
        break
      case 'Matching':
        nextStep = project?.discoverable
          ? {
              verb: 'match',
              icon: '🔗',
              headline: 'Discovery is on — we’re looking for overlaps with other inventions.',
              rule: 'from the pipeline · topic overlap only, your content is never shared',
              cta: tabCta('View matches', 'matches'),
            }
          : {
              verb: 'enable discovery',
              icon: '🔗',
              headline: 'Make this invention discoverable to find potential overlaps.',
              rule: 'from the pipeline · topic overlap only, your content is never shared',
              cta: {
                label: 'Enable discovery',
                onClick: () => project && toggleDiscoverableMutation.mutate({ id: project.project_id, discoverable: true }),
              },
            }
        break
      case 'Disclosure':
        nextStep = {
          verb: 'disclose',
          icon: '🤝',
          headline: 'You have matches — review them and manage what you disclose.',
          rule: 'from the pipeline',
          cta: tabCta('View matches', 'matches'),
        }
        break
    }
  }

  // The pipeline stepper is the spine of the project workspace (Spec §8.1): each
  // stage points ONLY at a real tab so the persistent stepper can never lead to
  // a tab that does not exist.
  const STAGE_TAB: Record<string, ProjectTab> = {
    Upload: 'documents',
    Intake: 'overview',
    Analysis: 'ai-analysis',
    Assessment: 'ai-analysis',
    Review: 'review',
    Matching: 'matches',
    Disclosure: 'matches',
  }
  const pipelineSteps: WorkflowStep[] = workflowStages.map((s) => ({
    key: s.name,
    label: s.name,
    status: s.status,
    onSelect: () => setActiveTab(STAGE_TAB[s.name] ?? 'overview'),
  }))

  // ── What the Overview cards and the Claims lens's Assessment tab render ────
  // Every value below comes from a record that exists. Where the estate holds
  // nothing, the card is passed nothing and says so itself — no placeholder,
  // no plausible-looking substitute.
  const inventionKeyPoints = projectAnalysis?.key_themes ?? []
  // Bug #6678: a document whose file later went missing (s3_orphaned) is still
  // part of the invention and its assessment was already computed, so it is
  // counted here — filtering it out made the page read "0 documents, 0/0/0"
  // while the list and Atlas said 1. The Documents tab still marks it "Missing
  // file"; only the Evidence link above needs a retrievable file.
  const visibleDocuments = documents ?? []
  // A document marked as a reference or supporting part is deliberately NOT
  // part of what the invention asserts, which is why the assessment line
  // reports the two counts separately.
  //
  // Two read bugs, both fixed here. BOTH roles count as set aside: the engine's
  // own module pairs them (shared/component_role.py treats 'supporting' and
  // 'reference' together everywhere) and the figure's sentence names both —
  // "N more set aside as reference or supporting". And the role is read from
  // `authoritative_component_role` first, which is what
  // component_role.py:infer_component_role wrote; the bare `component_role`
  // beside it is the legacy label and may be stale.
  const setAsideDocuments = visibleDocuments.filter((d) => {
    const role = d.authoritative_component_role ?? d.component_role
    return role === 'reference' || role === 'supporting'
  })
  const countedDocuments = visibleDocuments.length - setAsideDocuments.length

  const rawAxes = (assessmentData?.assessment?.axes ?? {}) as Record<string, { label?: string; reasoning?: string } | number>
  const axisMeta = (key: string) => {
    const axis = rawAxes[key]
    return typeof axis === 'object' && axis ? axis : {}
  }
  const axisTone = (score: number): AssessmentAxis['tone'] => (score >= 80 ? 'ok' : score >= 70 ? 'info' : 'warn')
  const assessmentAxes: AssessmentAxis[] | undefined = assessmentScores
    ? [
        {
          key: 'novelty',
          label: 'Novelty',
          qualifier: 'whole invention',
          score: assessmentScores.ip_novelty,
          band: axisMeta('novelty').label,
          tone: axisTone(assessmentScores.ip_novelty),
        },
        {
          key: 'feasibility',
          label: 'Feasibility',
          score: assessmentScores.technical_feasibility,
          band: axisMeta('feasibility').label,
          tone: axisTone(assessmentScores.technical_feasibility),
        },
        {
          // The engine computes market potential, not protectability. Renaming
          // it on screen would put a number under a heading it does not mean.
          key: 'market_potential',
          label: 'Market potential',
          score: assessmentScores.market_feasibility,
          band: axisMeta('market_potential').label,
          tone: axisTone(assessmentScores.market_feasibility),
        },
      ]
    : undefined

  // "One document disagrees with the whole": a single document scoring well
  // above the invention read as one. Real numbers on both sides — the
  // document's own novelty report and the invention-level novelty score — and
  // shown only when the gap is wide enough to change what a reader would do.
  const inventionNoveltyScore = assessmentScores?.ip_novelty
  const dissentingDocument = (() => {
    if (inventionNoveltyScore == null) return null
    const scored = visibleDocuments
      .map((d) => ({ name: d.title || d.name || d.filename || 'Untitled document', score: d.novelty_report?.overall_novelty_score }))
      .filter((d): d is { name: string; score: number } => typeof d.score === 'number')
      .sort((a, b) => b.score - a.score)[0]
    if (!scored || scored.score - inventionNoveltyScore < 15) return null
    return { ...scored, inventionScore: inventionNoveltyScore }
  })()

  // The invention's topics, read by the AI from its documents — the tags under
  // the intake card. Never the project's name split up, never a guess.
  const inventionTopics = aggregateProjectTopics(visibleDocuments).slice(0, 8)

  // "Recent activity" — only timestamps that exist on real records, newest
  // first. A record without a date is not shown at all rather than dated today.
  const recentActivity = (() => {
    const items: { key: string; label: string; detail?: string; when: string; iso: string; kind?: 'assessment' | 'analysis' | 'document' }[] = []
    const fmt = (iso: string) =>
      new Date(iso).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Australia/Sydney' })
    const assessedAt = assessmentData?.assessment?.created_at
    if (assessedAt) items.push({ key: 'assessed', label: 'Assessment scored', when: fmt(assessedAt), iso: assessedAt, kind: 'assessment' })
    if (projectAnalysis?.generated_at) {
      const claimCount = projectClaims?.claims.length ?? 0
      items.push({
        key: 'analysed',
        label: 'Analysis completed',
        detail: claimCount ? `${claimCount} claim${claimCount === 1 ? '' : 's'}` : undefined,
        when: fmt(projectAnalysis.generated_at),
        iso: projectAnalysis.generated_at,
        kind: 'analysis',
      })
    }
    for (const doc of [...visibleDocuments].sort((a, b) => (b.created_at ?? '').localeCompare(a.created_at ?? '')).slice(0, 2)) {
      if (!doc.created_at) continue
      items.push({
        key: `doc-${doc.document_id}`,
        label: `${doc.title || doc.name || doc.filename || 'A document'} added`,
        when: fmt(doc.created_at),
        iso: doc.created_at,
        kind: 'document',
      })
    }
    return items.sort((a, b) => b.iso.localeCompare(a.iso)).slice(0, 4)
  })()

  const isReference = project.is_reference === true

  const assessmentScoredOn = assessmentData?.assessment?.created_at && assessmentAxes
    ? new Date(assessmentData.assessment.created_at).toLocaleDateString('en-AU', {
        day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Australia/Sydney',
      })
    : undefined

  const assessmentPanel = (
    <AssessmentPanel
      axes={assessmentAxes}
      documentsCounted={countedDocuments}
      documentsSetAside={setAsideDocuments.length}
      computedAt={
        assessmentData?.assessment?.created_at
          ? new Date(assessmentData.assessment.created_at).toLocaleDateString('en-AU', {
              day: 'numeric', month: 'short', year: 'numeric', timeZone: 'Australia/Sydney',
            })
          : undefined
      }
      reviewApproved={project.review_status === 'approved'}
      reasoning={['novelty', 'feasibility', 'market_potential'].map((k) => axisMeta(k).reasoning ?? '')}
      novelTogether={projectAnalysis?.novel_aspects}
      alreadyInTheArt={(projectAnalysis?.overlap_areas ?? []).map((o) =>
        typeof o === 'string' ? o : o.overlap_description,
      )}
      dissenting={dissentingDocument}
      insufficientBasisReason={isInsufficientBasis ? INSUFFICIENT_BASIS_REASON : undefined}
      onViewFull={() => { setLensTab('claims'); navigate(`/project/${projectId}?tab=ai-analysis`) }}
      onReviewWhatCounts={() => navigate(`/project/${projectId}?tab=documents`)}
    />
  )

  // The Claims lens's own tab strip (figure S1). Its four tabs are the
  // questions a reader asks WHILE looking at a claim; everything else is an
  // invention-scope tab and lives behind More, which leaves the lens.
  const LENS_TABS: TabItem<LensTab>[] = [
    { key: 'claims', label: 'Claims', icon: Layers },
    {
      key: 'research',
      label: TAB_LABELS.research,
      icon: Search,
      count: (priorArt?.related_papers?.length ?? 0) + (priorArt?.patent_matches?.length ?? 0),
    },
    { key: 'ai-analysis', label: TAB_LABELS['ai-analysis'], icon: Sparkles },
    { key: 'next-steps', label: TAB_LABELS['next-steps'], icon: Zap },
    ...PROJECT_TABS.filter((t) => !['ai-analysis', 'research', 'next-steps'].includes(t.key)),
  ]

  // ── Screen S1: the Claims lens, inside the Invention scope ────────────────
  // Placed AFTER the access gate (a non-member never sees the lens shell) and
  // BEFORE the folders/documents gate below, so the lens never waits on the
  // documents query it is forbidden to read: its claims come from the
  // per-reader claims route and nothing else.
  if (inClaimsLens && projectId) {
    const lensBody = () => {
      switch (lensTab) {
        case 'research':
          return <ProjectPriorArtPanel projectId={projectId} documentCount={(documents ?? []).length} />
        case 'ai-analysis':
          return assessmentPanel
        case 'next-steps':
          return <WhatNextSuggestions projectId={projectId} documentCount={visibleDocuments.length} />
        default:
          return (
            <ClaimsLens
              projectId={projectId}
              focusIndex={focusedIndex}
              closed={focusClosed}
              onFocus={setFocusClaim}
              keyPoints={inventionKeyPoints}
            />
          )
      }
    }
    return (
      <div>
        <LensSwitch lens="claims" onClaims={() => setFocusClaim(focusClaim)} evidenceHref={evidenceHref} />

        <PageHeader
          title={project.name || 'Invention'}
          description={
            `What this invention asserts — every claim across its ${projectClaims?.documentCount ?? 0} `
            + 'documents, each scored against the disclosure that supports it.'
          }
          helpKey="projects"
          status={docsLoading ? undefined : { label: activeStage ? activeStage.name : 'Complete', tone: activeStage ? 'info' : 'success' }}
          secondaryActions={!viewingAsAggregator ? (
            <>
              <button
                onClick={() => setShowShareProject(true)}
                className="rounded-lg border border-sky-200 bg-white px-3 py-2 text-sm font-medium text-iflow-700 hover:bg-sky-50"
              >
                Share
              </button>
              <button
                onClick={() => downloadInventionMutation.mutate(visibleDocuments)}
                disabled={downloadInventionMutation.isPending || visibleDocuments.length === 0}
                title={
                  visibleDocuments.length === 0
                    ? 'This invention has no documents to download yet'
                    : `Download all ${visibleDocuments.length} document${visibleDocuments.length === 1 ? '' : 's'}`
                }
                className="rounded-lg border border-sky-200 bg-white px-3 py-2 text-sm font-medium text-iflow-700 hover:bg-sky-50 disabled:opacity-50"
              >
                {downloadInventionMutation.isPending ? 'Downloading…' : '⬇ Download'}
              </button>
              <button
                onClick={() => referenceMutation.mutate(!isReference)}
                disabled={referenceMutation.isPending}
                title="Background material in your set, rather than something you are pursuing. It changes nothing about who can see this invention."
                className={
                  isReference
                    ? 'rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm font-medium text-amber-700 disabled:opacity-50'
                    : 'rounded-lg border border-sky-200 bg-white px-3 py-2 text-sm font-medium text-iflow-700 hover:bg-sky-50 disabled:opacity-50'
                }
              >
                {isReference ? 'Reference ✓' : '☆ Mark as Reference'}
              </button>
              <button
                onClick={handleFullReanalysis}
                disabled={regenerating}
                className="rounded-lg bg-iflow-700 px-3 py-2 text-sm font-medium text-white hover:bg-iflow-800 disabled:opacity-50"
              >
                {regenerating ? 'Re-running…' : '🔄 Re-run analysis'}
              </button>
            </>
          ) : undefined}
        />

        <ProgressiveTabBar
          tabs={LENS_TABS}
          primaryKeys={LENS_PRIMARY}
          activeTab={lensTab}
          onTabChange={(key) => {
            if (LENS_PRIMARY.includes(key)) setLensTab(key)
            // Every other tab is an INVENTION-scope tab, so choosing it leaves
            // the lens for the invention page rather than pretending the lens
            // owns it.
            else navigate(`/project/${projectId}?tab=${key}`)
          }}
        />

        <div className="mt-4">
          {claimsNextStep ? (
            <NextStepBanner
              at="Invention"
              verb={claimsNextStep.verb}
              icon={claimsNextStep.icon}
              tone="amber"
              headline={claimsNextStep.headline}
              rule={claimsNextStep.rule}
              cta={
                claimsNextStep.cta
                  ? {
                      label: claimsNextStep.cta.label,
                      to: claimsNextStep.cta.to,
                      onClick: claimsNextStep.cta.tab ? () => navigate(`/project/${projectId}?tab=${claimsNextStep.cta!.tab}`) : undefined,
                    }
                  : undefined
              }
            />
          ) : (
            <NextStepBanner
              at="Invention"
              verb=""
              none
              icon="✔"
              headline={
                (projectClaims?.claims.length ?? 0) === 0
                  ? 'Nothing to protect yet — this invention has no analysed claims.'
                  : 'Every claim in this invention has a document behind it.'
              }
              rule="from the claim analysis across this invention's disclosure"
            />
          )}
          {lensBody()}
        </div>

        {showShareProject && (
          <ShareModal
            resourceId={projectId}
            resourceType="project"
            onClose={() => {
              setShowShareProject(false)
              queryClient.invalidateQueries({ queryKey: ['permissions', projectId] })
            }}
          />
        )}
        {regenError && <p className="mt-2 text-xs text-red-600">{regenError}</p>}
        {inventionDownloadError && <p className="mt-2 text-xs text-red-600">{inventionDownloadError}</p>}
        {referenceMutation.isError && (
          <p className="mt-2 text-xs text-red-600">
            That did not save. This invention is unchanged — try again.
          </p>
        )}
      </div>
    )
  }

  if (foldersLoading || docsLoading) return <LoadingSkeleton rows={4} />


  return (
    <div>
      {/* The single crumb comes from the spine spec this page declared, rendered
          by the layout. The old page-level "Projects › <name>" row is gone. */}
      <LensSwitch
        lens="claims"
        onClaims={() => {
          const next = new URLSearchParams(searchParams)
          next.set('lens', 'claims')
          setSearchParams(next, { replace: true })
        }}
        evidenceHref={evidenceHref}
      />

      <PageHeader
        title={project?.name || 'Project'}
        description="The documents, analysis, matches and contacts for one invention — where it sits in the pipeline, and the one next step."
        helpKey="projects"
        status={{ label: activeStage ? activeStage.name : 'Complete', tone: activeStage ? 'info' : 'success' }}
        primaryAction={
          !viewingAsAggregator && (
            <button
              onClick={() => navigate(uploadLinkFor(projectId))}
              className="flex items-center gap-2 px-4 py-2 bg-iflow-700 text-white text-sm font-medium rounded-lg hover:bg-iflow-800 transition-colors"
            >
              <Upload size={16} /> Upload
            </button>
          )
        }
        secondaryActions={
          <>
            {project && !viewingAsAggregator && (
              // The figure draws a switch labelled "Discoverable", on or off.
              // The old control changed its own name with its state ("Hidden"),
              // so the reader could not tell the setting from the state.
              <button
                type="button"
                role="switch"
                aria-checked={!!project.discoverable}
                onClick={() => toggleDiscoverableMutation.mutate({ id: project.project_id, discoverable: !project.discoverable })}
                disabled={toggleDiscoverableMutation.isPending}
                title={project.discoverable
                  ? 'Discoverable — blind matching may consider this invention. Click to turn it off.'
                  : 'Not discoverable — click to let blind matching consider this invention. Your content is never shared.'}
                className="flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-sm font-medium text-gray-700 hover:bg-sky-50 disabled:opacity-50"
              >
                <span
                  aria-hidden
                  className={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors ${
                    project.discoverable ? 'bg-iflow-500' : 'bg-slate-300'
                  }`}
                >
                  <span className={`absolute h-3 w-3 rounded-full bg-white transition-all ${project.discoverable ? 'left-3.5' : 'left-0.5'}`} />
                </span>
                Discoverable
              </button>
            )}
            {/* ROUND 7, Addendum 7. The S3 figure draws "🔗 3 potential
                matches" on an invention whose own Project details row reads
                "Discoverable Off" — the live account's exact state — and the
                pill was gated on `discoverable`, so it never appeared.

                Discoverable governs whether OTHERS can find this invention. It
                does not govern whether its owner may see the matches already
                found for it. Gating the owner's own view on it hid a real,
                already-computed count from the one person entitled to it, and
                made the figure impossible to reach.

                The pill now renders on the count alone. It still never invents
                one: MatchSummaryPill returns null with no summary, says
                "Matching pending" before the workflow reaches Matching, and
                says "No matches yet" at zero.

                An aggregator reading someone else's invention through a
                portfolio grant keeps the old condition — see the matchSummary
                query above for why. */}
            {(!viewingAsAggregator || project?.discoverable) && (
              <MatchSummaryPill matchSummary={matchSummary} matchingReached={matchingReached} onOpenMatches={() => setActiveTab('matches')} />
            )}
            {!viewingAsAggregator && (
              <HeaderActionMenu
                actions={[
                  {
                    label: 'Edit the invention intake',
                    onClick: () => {
                      setActiveTab('overview')
                      setIntakeEditing(true)
                    },
                  },
                  { label: 'Share this invention', onClick: () => setShowShareProject(true) },
                  {
                    label: regenerating ? 'Re-running analysis…' : 'Re-run analysis',
                    onClick: () => { void handleFullReanalysis() },
                    disabled: regenerating,
                  },
                ]}
              />
            )}
          </>
        }
        banner={nextStep && (
          <NextStepBanner
            at={viewingAsAggregator ? 'Invention · portfolio view' : (nextStep.at ?? 'Invention')}
            verb={nextStep.verb}
            icon={nextStep.icon}
            tone="amber"
            none={nextStep.verb === 'nothing waiting'}
            headline={nextStep.headline}
            rule={nextStep.rule}
            cta={nextStep.cta}
          />
        )}
      />

      {/* Project-level Workflow Progress strip — persistent across all tabs so the
          inventor stays oriented when they leave the Orchestrator tab.
          Stages: Upload → Intake → Analysis → Assessment → Review → Matching. */}
      {projectId && (
        <RelatedInYourSet
          projectId={projectId}
          groups={cachedOverlap?.groups}
          projectNames={projectNames}
        />
      )}

      {projectId && (
        <PipelineStepper
          steps={pipelineSteps}
          reviewApproved={project?.review_status === 'approved'}
          onExplainLock={() => setActiveTab('matches')}
          // One approve control on the screen, and it is the banner's: the
          // figure puts no button in this card, and two identical buttons a
          // hundred pixels apart ask the reader which one is the real one.
          action={approveReviewMutation.isError && (
            <p className="mt-2 text-xs text-red-600" role="alert">
              {(approveReviewMutation.error as Error)?.message ?? 'Failed to approve'}
            </p>
          )}
        />
      )}

      {/* The tab strip sits on the page, not inside a card (figure S3): the
          Overview it opens on is a set of cards, and a card inside a card reads
          as a box nobody meant. Every other tab keeps its own card so its
          panels still sit on white. */}
      {projectId && (
        <div className="mt-4">
          <ProgressiveTabBar
            tabs={PROJECT_TABS}
            primaryKeys={PROJECT_PRIMARY}
            activeTab={resolvedActiveTab}
            onTabChange={setActiveTab}
          />
          <div className={resolvedActiveTab === 'overview' ? 'mt-4' : 'light-card mt-3 p-4'}>
            {((activeTab: ProjectTab): ReactNode => {
              switch (activeTab) {
                case 'overview':
                  return (
                    <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-3">
                      <div className="space-y-4 xl:col-span-2">
                        <InventionIntakeCard
                          intake={{
                            whatItIs: declarationUnreadable ? undefined : project.invention_declaration,
                            problem: project.invention_problem,
                            approach: project.invention_approach,
                          }}
                          topics={inventionTopics}
                          canEdit={!viewingAsAggregator}
                          declarationUnreadable={declarationUnreadable}
                          unreadableFields={intakeUnreadableFields}
                          editing={intakeEditing}
                          onEditingChange={setIntakeEditing}
                          onSave={async (next) => {
                            // UX-022: never send a field the user did not change, and
                            // never send the declaration while it is unreadable - an
                            // empty draft would overwrite the real, encrypted value.
                            const changes: Record<string, string> = {}
                            if (next.whatItIs !== undefined && !declarationUnreadable && next.whatItIs !== (project.invention_declaration ?? '')) changes.invention_declaration = next.whatItIs
                            if (next.problem !== undefined && next.problem !== (project.invention_problem ?? '')) changes.invention_problem = next.problem
                            if (next.approach !== undefined && next.approach !== (project.invention_approach ?? '')) changes.invention_approach = next.approach
                            if (Object.keys(changes).length > 0) await updateProject(projectId!, changes)
                            // The declaration anchors the analysis, so editing it
                            // makes the existing summary stale — the backend
                            // stamps project_summary_stale and the pipeline
                            // reads it; the page no longer nags a bar about it.
                            await queryClient.refetchQueries({ queryKey: ['projects'] })
                            await queryClient.refetchQueries({ queryKey: ['project', projectId] })
                          }}
                        />
                        {assessmentPanel}
                        <WhatNextSuggestions projectId={projectId!} documentCount={visibleDocuments.length} />
                      </div>
                      <ProjectContextRail
                        project={project}
                        owners={owners}
                        collaborators={collaborators}
                        documentCount={visibleDocuments.length}
                        folderCount={folders?.length ?? 0}
                        stageLabel={activeStage ? activeStage.name : 'Complete'}
                        viewer={{ id: user?.id, name: user?.name }}
                        viewerIsOwner={isOwner}
                        activity={recentActivity}
                      />
                    </div>
                  )
                case 'documents':
                  return (
                    <>
                      <ProjectWhatNextPanel projectId={projectId!} />
                    <div
                      onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
                      onDragLeave={(e) => { if (e.currentTarget === e.target || !e.currentTarget.contains(e.relatedTarget as Node)) setDragOver(false) }}
                      onDrop={handleInlineDrop}
                    >
                      {/* Drop zone overlay */}
                      {dragOver && (
                        <div className="border-2 border-dashed border-iflow-400 bg-white/30 rounded-xl p-8 mb-4 text-center pointer-events-none">
                          <FileUp size={32} className="mx-auto text-iflow-400 mb-2" />
                          <p className="text-sm text-iflow-600 font-medium">Drop files to upload to this project</p>
                        </div>
                      )}

                      {/* Inline upload progress */}
                      {inlineFiles.length > 0 && (
                        <div className="mb-4 space-y-2">
                          {inlineFiles.map((f, i) => (
                            <div key={i} className="flex items-center gap-3 light-card p-3">
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-gray-900 truncate">{f.file.name}</p>
                                <p className="text-xs text-gray-500">{(f.file.size / 1024).toFixed(1)} KB</p>
                              </div>
                              {f.status === 'uploading' && (
                                <div className="w-24">
                                  <div className="h-1.5 bg-sky-200 rounded-full overflow-hidden">
                                    <div className="h-full bg-iflow-500 rounded-full transition-all" style={{ width: `${f.progress}%` }} />
                                  </div>
                                </div>
                              )}
                              {f.status === 'complete' && <CheckCircle size={18} className="text-green-500 flex-shrink-0" />}
                              {f.status === 'error' && (
                                <span className="flex items-center gap-1 text-xs text-red-500 flex-shrink-0">
                                  <AlertCircle size={14} /> {f.error}
                                </span>
                              )}
                              {(f.status === 'complete' || f.status === 'error') && (
                                <button onClick={() => setInlineFiles((prev) => prev.filter((pf) => pf.file !== f.file))} className="text-gray-500 hover:text-gray-600 flex-shrink-0">
                                  <X size={14} />
                                </button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Toolbar: document count + New Folder + Add Files */}
                      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                        <p className="text-sm text-gray-500">
                          {docsError
                            ? 'Error loading documents'
                            : (() => {
                                const allDocs = documents || []
                                const visible = showOrphans ? allDocs : allDocs.filter((d) => !d.s3_orphaned)
                                const total = visible.length
                                const orphanCount = allDocs.filter((d) => d.s3_orphaned).length
                                const orphanSuffix = !showOrphans && orphanCount > 0 ? ` (${orphanCount} missing file${orphanCount === 1 ? '' : 's'} hidden)` : ''
                                if (!docFilter.trim()) return `${total} document(s)${orphanSuffix}`
                                const f = docFilter.trim().toLowerCase()
                                const matched = visible.filter((d) => {
                                  const t = (d.title || '').toLowerCase()
                                  const tags = (d.tags || []).join(' ').toLowerCase()
                                  const topics = (d.topics || []).join(' ').toLowerCase()
                                  return t.includes(f) || tags.includes(f) || topics.includes(f)
                                }).length
                                return `${matched} of ${total} document(s) match "${docFilter}"${orphanSuffix}`
                              })()}
                        </p>
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* STORY-DOC-SEARCH (Round 6): within-project document filter */}
                          <input
                            type="text"
                            value={docFilter}
                            onChange={(e) => setDocFilter(e.target.value)}
                            placeholder="Filter documents..."
                            className="px-3 py-1.5 text-sm bg-white/70 border border-sky-200 rounded-lg focus:outline-none focus:border-iflow-400 placeholder-gray-400"
                            aria-label="Filter documents in project"
                          />
                          {/* BUG-3221-FIX-S2D: toggle orphan visibility — hidden by default
                              so the inventor sees a clean list; clickable when they need
                              to identify which uploads need re-uploading. */}
                          {(documents || []).some((d) => d.s3_orphaned) && (
                            <button
                              onClick={() => setShowOrphans((v) => !v)}
                              className={`flex items-center gap-1.5 px-3 py-1.5 text-sm border rounded-lg transition-colors ${
                                showOrphans
                                  ? 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100'
                                  : 'text-gray-700 border-sky-200 hover:bg-white/50'
                              }`}
                              aria-pressed={showOrphans}
                              title={showOrphans ? 'Hide documents whose S3 file is missing' : 'Show documents whose S3 file is missing'}
                            >
                              <FileX size={14} /> {showOrphans ? 'Hide missing files' : 'Show missing files'}
                            </button>
                          )}
                          <button
                            onClick={() => handleFolderAction({ type: 'create', parentFolderId: null })}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 border border-sky-200 rounded-lg hover:bg-white/50 transition-colors"
                          >
                            <FolderPlus size={14} /> New Folder
                          </button>
                          <label className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-gray-700 border border-sky-200 rounded-lg hover:bg-white/50 cursor-pointer transition-colors">
                            <FileUp size={14} /> Add Files
                            <input
                              type="file"
                              multiple
                              className="hidden"
                              onChange={(e) => e.target.files && handleInlineFileInput(e.target.files)}
                            />
                          </label>
                          {/* Grid/list toggle */}
                          <div className="flex items-center border border-sky-200 rounded-lg overflow-hidden">
                            <button
                              onClick={() => { setDocView('list'); try { localStorage.setItem('innovateflow:doc-view:project', 'list') } catch { /* a browser with site data blocked keeps its default view */ } }}
                              className={`p-1.5 ${docView === 'list' ? 'bg-iflow-50 text-iflow-600' : 'text-gray-500 hover:text-gray-600'}`}
                              title="List view" aria-pressed={docView === 'list'}
                            ><List size={14} /></button>
                            <button
                              onClick={() => { setDocView('grid'); try { localStorage.setItem('innovateflow:doc-view:project', 'grid') } catch { /* a browser with site data blocked keeps its default view */ } }}
                              className={`p-1.5 ${docView === 'grid' ? 'bg-iflow-50 text-iflow-600' : 'text-gray-500 hover:text-gray-600'}`}
                              title="Grid view" aria-pressed={docView === 'grid'}
                            ><LayoutGrid size={14} /></button>
                          </div>
                        </div>
                      </div>

                      {docsError ? (
                        <div className="light-card p-4 text-sm text-red-700 border-red-300/30">
                          <p className="font-medium">Failed to load documents</p>
                          <p className="mt-1 text-red-600">
                            {docsError instanceof Error && docsError.message === 'Network Error'
                              ? 'Unable to reach the server. Check your internet connection and try refreshing the page. If the problem persists, your session may have expired — try signing out and back in.'
                              : docsError instanceof Error
                                ? docsError.message
                                : 'An unexpected error occurred. Please try refreshing the page.'}
                          </p>
                          <button
                            onClick={() => refetchDocs()}
                            className="mt-2 text-xs font-medium text-red-700 underline hover:text-red-900"
                          >
                            Retry
                          </button>
                        </div>
                      ) : !documents?.length && !inlineFiles.length && !(folders?.filter(f => !f.parent_folder_id || f.parent_folder_id === 'root').length) ? (
                        <EmptyState
                          icon={<Upload size={24} />}
                          title="No documents"
                          description="Drag & drop files here or click Add Files to upload."
                        />
                      ) : (
                        <div className="space-y-3">
                          {/* Inline folders at top — filter doesn't apply to folders, they remain
                              visible so the user can drill in if their search lives inside one. */}
                          {folders?.filter(f => !f.parent_folder_id || f.parent_folder_id === 'root').map((folder) => (
                            <FolderRow key={folder.folder_id} folder={folder} projectId={projectId!} onAction={handleFolderAction} />
                          ))}
                          {/* STORY-DOC-SEARCH (Round 6): apply title/tags/topics filter
                              BUG-3221-FIX-S2D: also hide s3_orphaned by default. */}
                          {(() => {
                            const f = docFilter.trim().toLowerCase()
                            const orphanFiltered = showOrphans
                              ? (documents || [])
                              : (documents || []).filter((d) => !d.s3_orphaned)
                            const filtered = !f
                              ? orphanFiltered
                              : orphanFiltered.filter((d) => {
                                  const t = (d.title || '').toLowerCase()
                                  const tags = (d.tags || []).join(' ').toLowerCase()
                                  const topics = (d.topics || []).join(' ').toLowerCase()
                                  return t.includes(f) || tags.includes(f) || topics.includes(f)
                                })
                            if (f && filtered.length === 0) {
                              return (
                                <div className="light-card p-4 text-sm text-gray-600">
                                  No documents match "{docFilter}".
                                  <button
                                    onClick={() => setDocFilter('')}
                                    className="ml-2 text-xs font-medium text-iflow-600 underline hover:text-iflow-700"
                                  >
                                    Clear filter
                                  </button>
                                </div>
                              )
                            }
                            // BUG: when every document in the project is s3_orphaned (the
                            // underlying S3 file is missing), the toolbar above correctly says
                            // "0 document(s) (N missing files hidden)" but this list rendered
                            // NOTHING below it — the "Missing file" flag is a real doc-health
                            // signal worth keeping hidden by default, but a silent blank area
                            // reads as broken, not as "everything is fine, nothing to see".
                            // Give an honest, actionable state instead: explain the files are
                            // missing, and offer both the reveal toggle and the re-upload path.
                            if (!f && !showOrphans && filtered.length === 0 && (documents || []).length > 0) {
                              const missingCount = (documents || []).length
                              return (
                                <div className="light-card p-4 flex items-start gap-3 border-red-200/60" data-testid="all-orphaned-empty-state">
                                  <FileX size={20} className="text-red-500 mt-0.5 flex-shrink-0" />
                                  <div>
                                    <p className="text-sm font-medium text-gray-800">
                                      {missingCount === 1 ? 'This document is missing its file' : `All ${missingCount} documents are missing their files`}
                                    </p>
                                    <p className="text-xs text-gray-600 mt-1">
                                      {missingCount === 1 ? 'Its' : 'Their'} underlying file could not be found in storage — this can happen if an
                                      upload was interrupted or the file was later removed. Re-upload the original file to restore{' '}
                                      {missingCount === 1 ? 'it' : 'them'}, or view the missing entr{missingCount === 1 ? 'y' : 'ies'} below.
                                    </p>
                                    <div className="mt-2 flex items-center gap-3">
                                      <button
                                        onClick={() => setShowOrphans(true)}
                                        className="text-xs font-medium text-iflow-600 underline hover:text-iflow-700"
                                      >
                                        Show missing file{missingCount === 1 ? '' : 's'}
                                      </button>
                                      <label className="text-xs font-medium text-iflow-600 underline hover:text-iflow-700 cursor-pointer">
                                        Re-upload a file
                                        <input
                                          type="file"
                                          multiple
                                          className="hidden"
                                          onChange={(e) => e.target.files && handleInlineFileInput(e.target.files)}
                                        />
                                      </label>
                                    </div>
                                  </div>
                                </div>
                              )
                            }
                            if (docView === 'grid') {
                              return <DocumentGrid documents={filtered} />
                            }
                            return filtered.map((doc) => (
                              <DocumentCard key={doc.document_id} document={doc} />
                            ))
                          })()}
                        </div>
                      )}
                    </div>
                    </>
                  )
                case 'research':
                  return (
                    <div>
                      {/* Sub-tabs */}
                      <div className="flex border-b border-gray-200 mb-4">
                        <button
                          onClick={() => setResearchSubTab('prior-art')}
                          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                            researchSubTab === 'prior-art'
                              ? 'border-iflow-500 text-iflow-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          Overlapping Prior Art
                        </button>
                        <button
                          onClick={() => setResearchSubTab('related-research')}
                          className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                            researchSubTab === 'related-research'
                              ? 'border-iflow-500 text-iflow-600'
                              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                          }`}
                        >
                          Related Research
                        </button>
                      </div>
                      {researchSubTab === 'prior-art' ? (
                        <ProjectPriorArtPanel projectId={projectId!} documentCount={(documents ?? []).length} />
                      ) : (
                        <RelatedJournalsPanel projectId={projectId} folderId={projectId} priorArtPaperIds={priorArtPaperIds} priorArtPaperTitles={priorArtPaperTitles} />
                      )}
                    </div>
                  )
                case 'collaboration':
                  return <CollaborationPanel scope={{ type: 'project', projectId }} />
                case 'notes':
                  return <FolderNotesList projectId={projectId} folderId={projectId} />
                case 'connections':
                  return (
                    <>
                      <ProjectWhatNextPanel projectId={projectId!} />
                      {documents && documents.length > 0
                        ? <InlineConnectionsPanel resourceId={documents[0].document_id} title="Project Connections" projectId={projectId} />
                        : <p className="text-xs text-gray-500">No documents to show connections for</p>}
                    </>
                  )
                case 'topics': {
                  const topics = aggregateProjectTopics(documents || [])
                  return topics.length > 0
                    ? <div className="flex flex-wrap gap-2">{topics.map((t) => <span key={t} className="px-2 py-1 text-xs bg-iflow-100 text-iflow-700 rounded-full">{t}</span>)}</div>
                    : <p className="text-xs text-gray-500">No topics extracted yet</p>
                }
                case 'shared':
                  return (
                    <div className="space-y-6">
                      {/* Owners section */}
                      <div>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Crown size={14} className="text-amber-500" />
                            <h3 className="text-sm font-semibold text-gray-800">Owners</h3>
                          </div>
                          {isManager && (
                            <button
                              onClick={() => { setShowAddOwner(!showAddOwner); setOwnerError('') }}
                              className="p-1 text-gray-500 hover:text-iflow-600 transition-colors"
                              title="Add owner"
                            >
                              <UserPlus size={16} />
                            </button>
                          )}
                        </div>
                        {ownerError && (
                          <div className="mb-2 p-2 bg-red-500/10 border border-red-300/30 rounded text-xs text-red-700">
                            {ownerError}
                          </div>
                        )}
                        {showAddOwner && isManager && (
                          <div className="mb-3 flex gap-2">
                            <input
                              type="email"
                              value={newOwnerEmail}
                              onChange={(e) => setNewOwnerEmail(e.target.value)}
                              onKeyDown={(e) => e.key === 'Enter' && newOwnerEmail.trim() && addOwnerMutation.mutate(newOwnerEmail.trim())}
                              className="flex-1 px-2 py-1.5 text-xs light-input focus:ring-1 focus:ring-iflow-500 outline-none"
                              placeholder="owner@example.com"
                              autoFocus
                            />
                            <button
                              onClick={() => addOwnerMutation.mutate(newOwnerEmail.trim())}
                              disabled={!newOwnerEmail.trim() || addOwnerMutation.isPending}
                              className="px-2.5 py-1.5 text-xs text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 disabled:opacity-50"
                            >
                              {addOwnerMutation.isPending ? '...' : 'Add'}
                            </button>
                          </div>
                        )}
                        {!owners?.length ? (
                          <p className="text-xs text-gray-500">Loading owners...</p>
                        ) : (
                          <div className="space-y-2">
                            {owners.map((owner) => (
                              <div key={owner.user_id} className="flex items-center justify-between group/owner">
                                <div className="flex items-center gap-2 min-w-0">
                                  <div className="w-6 h-6 rounded-full bg-amber-100 text-amber-600 flex items-center justify-center text-xs font-medium flex-shrink-0">
                                    {(owner.email || '?').charAt(0).toUpperCase()}
                                  </div>
                                  <div className="min-w-0">
                                    <span className="text-xs text-gray-700 truncate block">{owner.email}</span>
                                    <span className="text-[10px] text-amber-600 font-medium">Owner</span>
                                  </div>
                                </div>
                                {isManager && (
                                  <button
                                    onClick={() => removeOwnerMutation.mutate(owner.user_id)}
                                    disabled={owners.length <= 1}
                                    className="p-0.5 text-gray-300 hover:text-red-500 opacity-0 group-hover/owner:opacity-100 transition-opacity disabled:opacity-30 disabled:cursor-not-allowed"
                                    title={owners.length <= 1 ? 'At least one owner required' : 'Remove owner'}
                                  >
                                    <span className="text-xs">&times;</span>
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>

                      {/* Collaborators section */}
                      <div className="border-t border-sky-200/30 pt-4">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Users size={14} className="text-gray-500" />
                            <h3 className="text-sm font-semibold text-gray-800">Collaborators</h3>
                          </div>
                          {isManager && (
                            <button
                              onClick={() => setShowShareProject(true)}
                              className="p-1 text-gray-500 hover:text-iflow-600 transition-colors"
                              title="Add collaborator"
                            >
                              <UserPlus size={16} />
                            </button>
                          )}
                        </div>
                        {!displayCollaborators.length ? (
                          <div>
                            <p className="text-xs text-gray-500 mb-2">No collaborators yet.</p>
                            {isManager && (
                              <button
                                onClick={() => setShowShareProject(true)}
                                className="text-xs text-iflow-600 hover:text-iflow-700 font-medium"
                              >
                                + Invite by email
                              </button>
                            )}
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {displayCollaborators.map((perm) => (
                              <div key={perm.grantee_id} className="flex items-center justify-between group/collab">
                                <div className="flex items-center gap-2 min-w-0">
                                  <div className="w-6 h-6 rounded-full bg-iflow-100 text-iflow-600 flex items-center justify-center text-xs font-medium flex-shrink-0">
                                    {granteeInitial(perm)}
                                  </div>
                                  <span className="text-xs text-gray-700 truncate">{granteeLabel(perm)}</span>
                                </div>
                                {'fromDisclosure' in perm && perm.fromDisclosure ? (
                                  // Disclosure-granted access is governed by the agreement, not the
                                  // permissions table — show it as a read-only Full Collaboration badge
                                  // (no edit/revoke here; that happens via the disclosure flow).
                                  <span
                                    className="text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 whitespace-nowrap"
                                    title="Access granted via Full Collaboration disclosure agreement"
                                  >
                                    Full Collaboration
                                  </span>
                                ) : (
                                  <div className="flex items-center gap-1">
                                    <button
                                      onClick={() => setPermModalCollaborator({ grantee_id: perm.grantee_id, grantee_email: perm.grantee_email || perm.grantee_id })}
                                      className="text-xs text-iflow-500 capitalize underline decoration-dotted underline-offset-2 hover:text-iflow-600 hover:decoration-solid cursor-pointer transition-colors"
                                      title="Click to edit permissions"
                                    >
                                      {perm.permission_level}
                                    </button>
                                    {isManager && (
                                      <button
                                        onClick={() => removeCollaboratorMutation.mutate(perm.grantee_id)}
                                        className="p-0.5 text-gray-300 hover:text-red-500 opacity-0 group-hover/collab:opacity-100 transition-opacity"
                                        title="Remove"
                                      >
                                        <span className="text-xs">&times;</span>
                                      </button>
                                    )}
                                  </div>
                                )}
                              </div>
                            ))}
                            {isManager && (
                              <button
                                onClick={() => setShowShareProject(true)}
                                className="text-xs text-iflow-600 hover:text-iflow-700 font-medium mt-1"
                              >
                                + Add more
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )
                case 'activity':
                  return <ProjectActivityTimeline projectId={projectId} />
                case 'next-steps':
                  return <OrchestratorTab projectId={projectId} documentIds={documents?.map(d => d.document_id) || []} />
                case 'review':
                  return (
                    <ProjectReview
                      projectId={projectId!}
                      documents={documents || []}
                      owners={owners || []}
                      collaborators={collaborators || []}
                    />
                  )
                case 'ai-analysis':
                  return (
                    <div className="grid grid-cols-1 xl:grid-cols-3 gap-4">
                      <div className="xl:col-span-2 space-y-6">
                        <div>
                          <h3 className="text-sm font-semibold text-iflow-900 mb-2">Overall analysis summary</h3>
                          <ProjectAIAnalysisPanel projectId={projectId!} inventionDeclaration={project?.invention_declaration} declarationUnreadable={declarationUnreadable} onAddDeclaration={() => { setActiveTab('overview'); window.scrollTo({ top: 0, behavior: 'smooth' }) }} project={project} documents={documents} priorArt={priorArtArticles} analysisTimedOut={analysisPollTimedOut} assessmentScoredOn={assessmentScoredOn} />
                        </div>
                      </div>
                      <div>
                        <ProjectContextRail
                          project={project}
                          owners={owners}
                          collaborators={collaborators}
                          documentCount={visibleDocuments.length}
                          folderCount={folders?.length ?? 0}
                          stageLabel={activeStage ? activeStage.name : 'Complete'}
                          viewer={{ id: user?.id, name: user?.name }}
                          viewerIsOwner={isOwner}
                          activity={recentActivity}
                        />
                      </div>
                    </div>
                  )
                case 'whitespace':
                  return (
                    <div className="space-y-3">
                      {landedFromOverlapNotify && (
                        <div
                          className="flex items-start gap-2 p-3 bg-sky-50 border border-sky-200 rounded-lg text-xs text-iflow-800"
                          data-testid="overlap-notify-landing-banner"
                        >
                          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5 text-iflow-600" />
                          <div>
                            <p>
                              A cross-project overlap in your portfolio was flagged involving your project and
                              another project.{' '}
                              <HelpPopover topic="inv-overlap-notify" label="Why am I seeing this?" size={13} />
                            </p>
                          </div>
                        </div>
                      )}
                      <WhiteSpaceTab projectId={projectId!} onNavigateToNoveltyProbe={() => setActiveTab('ai-analysis')} />
                    </div>
                  )
                case 'market':
                  return <MarketTab projectId={projectId!} />
                case 'reference-components':
                  return <ReferenceComponentsList projectId={projectId!} />
                case 'aggregators':
                  return <AggregatorSharingPanel projectId={projectId!} />
                case 'matches':
                  return (
                    <div className="space-y-6">
                      <MatchesTab
                        projectId={projectId!}
                        discoverable={!!project?.discoverable}
                        reviewApproved={project?.review_status === 'approved'}
                        matchSummary={matchSummary}
                        isPollingTimedOut={matchPollTimedOut}
                        matchingReached={matchingReached}
                        onFlipDiscoverable={() => project && toggleDiscoverableMutation.mutate({ id: project.project_id, discoverable: !project.discoverable })}
                        onRecomputeStarted={() => { setMatchPollTimedOut(false); pollStartedAt.current = null }}
                        canApproveReview={canApproveReview(isOwner, hasAssessmentScores, project?.review_status)}
                        isOwner={isOwner}
                        ownerNames={(owners ?? []).map((o) => o.name || o.email.split('@')[0])}
                        onApproveReview={() => projectId && approveReviewMutation.mutate(projectId)}
                        approvePending={approveReviewMutation.isPending}
                        approveError={approveReviewMutation.isError ? ((approveReviewMutation.error as Error)?.message ?? 'Failed to approve') : null}
                      />
                      <DisclosureLadder
                        projectId={projectId!}
                        discoverable={!!project?.discoverable}
                        matchRunComplete={matchSummary?.run_status === 'complete'}
                      />
                    </div>
                  )
              }
            })(resolvedActiveTab)}
          </div>
        </div>
      )}

      {/* Create Folder Modal */}
      {showCreateFolder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowCreateFolder(false)} />
          <div className="relative bg-[#0c4a6e] rounded-xl border border-white/20 backdrop-blur-md shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">
              {createParentId ? 'Create Subfolder' : 'Create Folder'}
            </h3>
            {createParentId && (
              <p className="text-sm text-white/60 mb-3">
                Inside: <span className="font-medium text-white/80">{folders?.find((f) => f.folder_id === createParentId)?.name || 'folder'}</span>
              </p>
            )}
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">Folder Name</label>
              <input
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && newFolderName.trim() && createFolderMutation.mutate({ name: newFolderName.trim(), parent_folder_id: createParentId })}
                className="w-full px-3 py-2 glass-input text-sm outline-none"
                placeholder="e.g. Patent Drafts"
                autoFocus
              />
            </div>
            {folderError && <p className="text-sm text-red-300 mt-2">{folderError}</p>}
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setShowCreateFolder(false)} className="px-4 py-2 text-sm text-white/80 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20">
                Cancel
              </button>
              <button
                onClick={() => createFolderMutation.mutate({ name: newFolderName.trim(), parent_folder_id: createParentId })}
                disabled={!newFolderName.trim() || createFolderMutation.isPending}
                className="px-4 py-2 text-sm text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 disabled:opacity-50"
              >
                {createFolderMutation.isPending ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Folder Modal */}
      {renameFolder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setRenameFolder(null)} />
          <div className="relative bg-[#0c4a6e] rounded-xl border border-white/20 backdrop-blur-md shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-white mb-4">Rename Folder</h3>
            <div>
              <label className="block text-sm font-medium text-white/80 mb-1">New Name</label>
              <input
                value={renameName}
                onChange={(e) => setRenameName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && renameName.trim() && renameFolderMutation.mutate({ folderId: renameFolder.folder_id, name: renameName.trim() })}
                className="w-full px-3 py-2 glass-input text-sm outline-none"
                autoFocus
              />
            </div>
            {folderError && <p className="text-sm text-red-300 mt-2">{folderError}</p>}
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setRenameFolder(null)} className="px-4 py-2 text-sm text-white/80 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20">
                Cancel
              </button>
              <button
                onClick={() => renameFolderMutation.mutate({ folderId: renameFolder.folder_id, name: renameName.trim() })}
                disabled={!renameName.trim() || renameName === renameFolder.name || renameFolderMutation.isPending}
                className="px-4 py-2 text-sm text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 disabled:opacity-50"
              >
                {renameFolderMutation.isPending ? 'Renaming...' : 'Rename'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Move Folder Modal */}
      {moveFolder && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMoveFolder(null)} />
          <div className="relative bg-[#0c4a6e] rounded-xl border border-white/20 backdrop-blur-md shadow-xl max-w-md w-full mx-4 p-6">
            <h3 className="text-lg font-semibold text-white mb-1">Move Folder</h3>
            <p className="text-sm text-white/60 mb-4">
              Moving: <span className="font-medium text-white/80">{moveFolder.name}</span>
            </p>
            <div>
              <label className="block text-sm font-medium text-white/80 mb-2">Destination</label>
              <div className="border border-white/20 rounded-lg max-h-48 overflow-y-auto">
                <button
                  onClick={() => setMoveTargetId(null)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${
                    moveTargetId === null ? 'bg-white/15 text-white font-medium' : 'text-white/70 hover:bg-white/10'
                  }`}
                >
                  <Home size={14} /> Project Root
                </button>
                {getMoveTargets(moveFolder).map((f) => (
                  <button
                    key={f.folder_id}
                    onClick={() => setMoveTargetId(f.folder_id)}
                    className={`w-full flex items-center gap-2 px-3 py-2 text-sm ${
                      moveTargetId === f.folder_id ? 'bg-white/15 text-white font-medium' : 'text-white/70 hover:bg-white/10'
                    }`}
                    style={{ paddingLeft: `${(f.materialized_path.split('/').length - 1) * 12 + 12}px` }}
                  >
                    <FolderPlus size={14} /> {f.name}
                  </button>
                ))}
              </div>
            </div>
            {folderError && <p className="text-sm text-red-300 mt-2">{folderError}</p>}
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setMoveFolder(null)} className="px-4 py-2 text-sm text-white/80 bg-white/10 border border-white/20 rounded-lg hover:bg-white/20">
                Cancel
              </button>
              <button
                onClick={() => moveFolderMutation.mutate({ folderId: moveFolder.folder_id, parentId: moveTargetId })}
                disabled={moveTargetId === moveFolder.parent_folder_id || moveFolderMutation.isPending}
                className="px-4 py-2 text-sm text-white bg-iflow-700 rounded-lg hover:bg-iflow-800 disabled:opacity-50"
              >
                {moveFolderMutation.isPending ? 'Moving...' : 'Move'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Share Project Modal */}
      {showShareProject && (
        <ShareModal
          resourceId={projectId!}
          resourceType="project"
          onClose={() => {
            setShowShareProject(false)
            queryClient.invalidateQueries({ queryKey: ['permissions', projectId] })
          }}
        />
      )}

      {/* Collaborator Permissions Modal */}
      <CollaboratorPermissionsModal
        isOpen={!!permModalCollaborator}
        onClose={() => setPermModalCollaborator(null)}
        resourceId={projectId!}
        resourceType="project"
        resourceName={project?.name || 'Project'}
        granteeId={permModalCollaborator?.grantee_id || ''}
        granteeEmail={permModalCollaborator?.grantee_email || ''}
        isManager={isManager}
        onUpdated={() => queryClient.invalidateQueries({ queryKey: ['permissions', projectId] })}
      />

      {/* Delete Folder Confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete Folder"
        message={`Are you sure you want to delete "${deleteTarget?.name}"? The folder must be empty.${folderError ? `\n\nError: ${folderError}` : ''}`}
        confirmLabel="Delete"
        variant="danger"
        onConfirm={() => deleteTarget && deleteFolderMutation.mutate(deleteTarget.folder_id)}
        onCancel={() => { setDeleteTarget(null); setFolderError('') }}
      />
    </div>
  )
}
